import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import { useCallback } from 'react';

import type {
  APIAudienceDTO,
  APIPermissionDTO,
  ApiDTO,
  ApplicationDTO,
  ApplicationRoleIDDTO,
  ApplicationURIDTO,
  BadRequestResponse,
  ConflictResponse,
  CreateAPIRequestDTO,
  CreateApplicationRequestDTO,
  CreateOrganizationRequestDTO,
  CreateRoleRequestDTO,
  CreateTenantApplicationRequestDTO,
  CreateTenantOrganizationRequestDTO,
  CreateTenantRequestDTO,
  CreateTenantUserProfileRequestDTO,
  CreateUserConflictResponse,
  CreateUserProfileRequestDTO,
  CreateUserSuccessResponseDTO,
  FindUsersParams,
  ForbiddenResponse,
  GenerateAccessTokenParams,
  GetApplicationIconParams,
  GetHealth200,
  GetTenantIconParams,
  GetTenantUserProfileParams,
  IconResponse,
  IconUploadBodies,
  IconUploadResponseDTO,
  InviteUserConflictResponse,
  InviteUserRequestDTO,
  InviteUserSuccessResponseDTO,
  InviteUserWithUserProfileRequestDTO,
  ListAvailableUserProfilesParams,
  ManageFindUsersParams,
  ManageInviteUserWithUserProfileRequestDTO,
  NotFoundResponse,
  OrganizationDTO,
  PayloadTooLargeResponse,
  RoleDTO,
  RolePermissionDTO,
  TenantApplicationDTO,
  TenantDTO,
  TenantIDDTO,
  TenantIDPOnlyDomainDTO,
  TokenDTO,
  UnauthorizedResponse,
  UpdateAPIAudiencesRequestDTO,
  UpdateAPIPermissionsRequestDTO,
  UpdateAPIRequestDTO,
  UpdateApplicationRequestDTO,
  UpdateApplicationRolesRequestDTO,
  UpdateApplicationURIsRequestDTO,
  UpdateOrganizationActiveRequestDTO,
  UpdateOrganizationRequestDTO,
  UpdateRolePermissionsRequestDTO,
  UpdateRoleRequestDTO,
  UpdateTenantApplicationRequestDTO,
  UpdateTenantRequestDTO,
  UpdateUserProfileRequestDTO,
  UserDTO,
  UserProfileConflictResponse,
  UserProfileDTO,
  WellKnownJwks200,
} from './models';

import { useAxiosAuth } from '../../hooks/useAxiosAuth';
export const useGetHealthHook = () => {
  const getHealth = useAxiosAuth<GetHealth200>();

  return useCallback(
    (signal?: AbortSignal) => {
      return getHealth({ url: `/health`, method: 'GET', signal });
    },
    [getHealth],
  );
};

export const getGetHealthQueryKey = () => {
  return [`/health`] as const;
};

export const useGetHealthQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetHealthHook>>>,
  TError = unknown,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<ReturnType<typeof useGetHealthHook>>>,
      TError,
      TData
    >
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetHealthQueryKey();

  const getHealth = useGetHealthHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGetHealthHook>>>
  > = ({ signal }) => getHealth(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetHealthHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetHealthQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetHealthHook>>>
>;
export type GetHealthQueryError = unknown;

export function useGetHealth<
  TData = Awaited<ReturnType<ReturnType<typeof useGetHealthHook>>>,
  TError = unknown,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetHealthHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetHealthHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetHealthHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetHealth<
  TData = Awaited<ReturnType<ReturnType<typeof useGetHealthHook>>>,
  TError = unknown,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetHealthHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetHealthHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetHealthHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetHealth<
  TData = Awaited<ReturnType<ReturnType<typeof useGetHealthHook>>>,
  TError = unknown,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetHealthHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };

export function useGetHealth<
  TData = Awaited<ReturnType<ReturnType<typeof useGetHealthHook>>>,
  TError = unknown,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetHealthHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useGetHealthQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * This endpoint provides the JSON Web Key Set (JWKS)
which includes the public keys used to verify
JWT tokens issued by this server.

 * @summary .well-known route to retrieve the JSON Web Key Set (JWKS)
 */
export const useWellKnownJwksHook = () => {
  const wellKnownJwks = useAxiosAuth<WellKnownJwks200>();

  return useCallback(
    (signal?: AbortSignal) => {
      return wellKnownJwks({
        url: `/.well-known/jwks.json`,
        method: 'GET',
        signal,
      });
    },
    [wellKnownJwks],
  );
};

export const getWellKnownJwksQueryKey = () => {
  return [`/.well-known/jwks.json`] as const;
};

export const useWellKnownJwksQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useWellKnownJwksHook>>>,
  TError = unknown,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<ReturnType<typeof useWellKnownJwksHook>>>,
      TError,
      TData
    >
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getWellKnownJwksQueryKey();

  const wellKnownJwks = useWellKnownJwksHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useWellKnownJwksHook>>>
  > = ({ signal }) => wellKnownJwks(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useWellKnownJwksHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type WellKnownJwksQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useWellKnownJwksHook>>>
>;
export type WellKnownJwksQueryError = unknown;

export function useWellKnownJwks<
  TData = Awaited<ReturnType<ReturnType<typeof useWellKnownJwksHook>>>,
  TError = unknown,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useWellKnownJwksHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useWellKnownJwksHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useWellKnownJwksHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useWellKnownJwks<
  TData = Awaited<ReturnType<ReturnType<typeof useWellKnownJwksHook>>>,
  TError = unknown,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useWellKnownJwksHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useWellKnownJwksHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useWellKnownJwksHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useWellKnownJwks<
  TData = Awaited<ReturnType<ReturnType<typeof useWellKnownJwksHook>>>,
  TError = unknown,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useWellKnownJwksHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary .well-known route to retrieve the JSON Web Key Set (JWKS)
 */

export function useWellKnownJwks<
  TData = Awaited<ReturnType<ReturnType<typeof useWellKnownJwksHook>>>,
  TError = unknown,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useWellKnownJwksHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useWellKnownJwksQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Lists available user profiles.
Optionally takes an application_uri to list user profiles for.
If no application_uri is provided,
all user profiles for the user are retrieved

 * @summary List available user profiles
 */
export const useListAvailableUserProfilesHook = () => {
  const listAvailableUserProfiles = useAxiosAuth<UserProfileDTO[]>();

  return useCallback(
    (params?: ListAvailableUserProfilesParams, signal?: AbortSignal) => {
      return listAvailableUserProfiles({
        url: `/profiles`,
        method: 'GET',
        params,
        signal,
      });
    },
    [listAvailableUserProfiles],
  );
};

export const getListAvailableUserProfilesQueryKey = (
  params?: ListAvailableUserProfilesParams,
) => {
  return [`/profiles`, ...(params ? [params] : [])] as const;
};

export const useListAvailableUserProfilesQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useListAvailableUserProfilesHook>>
  >,
  TError = BadRequestResponse | UnauthorizedResponse | NotFoundResponse,
>(
  params?: ListAvailableUserProfilesParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useListAvailableUserProfilesHook>>
        >,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListAvailableUserProfilesQueryKey(params);

  const listAvailableUserProfiles = useListAvailableUserProfilesHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useListAvailableUserProfilesHook>>>
  > = ({ signal }) => listAvailableUserProfiles(params, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useListAvailableUserProfilesHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type ListAvailableUserProfilesQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useListAvailableUserProfilesHook>>>
>;
export type ListAvailableUserProfilesQueryError =
  | BadRequestResponse
  | UnauthorizedResponse
  | NotFoundResponse;

export function useListAvailableUserProfiles<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListAvailableUserProfilesHook>>
  >,
  TError = BadRequestResponse | UnauthorizedResponse | NotFoundResponse,
>(
  params: undefined | ListAvailableUserProfilesParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useListAvailableUserProfilesHook>>
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useListAvailableUserProfilesHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useListAvailableUserProfilesHook>>
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useListAvailableUserProfiles<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListAvailableUserProfilesHook>>
  >,
  TError = BadRequestResponse | UnauthorizedResponse | NotFoundResponse,
>(
  params?: ListAvailableUserProfilesParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useListAvailableUserProfilesHook>>
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useListAvailableUserProfilesHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useListAvailableUserProfilesHook>>
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useListAvailableUserProfiles<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListAvailableUserProfilesHook>>
  >,
  TError = BadRequestResponse | UnauthorizedResponse | NotFoundResponse,
>(
  params?: ListAvailableUserProfilesParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useListAvailableUserProfilesHook>>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary List available user profiles
 */

export function useListAvailableUserProfiles<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListAvailableUserProfilesHook>>
  >,
  TError = BadRequestResponse | UnauthorizedResponse | NotFoundResponse,
>(
  params?: ListAvailableUserProfilesParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useListAvailableUserProfilesHook>>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useListAvailableUserProfilesQueryOptions(
    params,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Generate an access token for a profile
 */
export const useGenerateAccessTokenHook = () => {
  const generateAccessToken = useAxiosAuth<TokenDTO>();

  return useCallback(
    (params: GenerateAccessTokenParams, signal?: AbortSignal) => {
      return generateAccessToken({
        url: `/profiles/token`,
        method: 'GET',
        params,
        signal,
      });
    },
    [generateAccessToken],
  );
};

export const getGenerateAccessTokenQueryKey = (
  params?: GenerateAccessTokenParams,
) => {
  return [`/profiles/token`, ...(params ? [params] : [])] as const;
};

export const useGenerateAccessTokenQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGenerateAccessTokenHook>>>,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(
  params: GenerateAccessTokenParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGenerateAccessTokenHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGenerateAccessTokenQueryKey(params);

  const generateAccessToken = useGenerateAccessTokenHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGenerateAccessTokenHook>>>
  > = ({ signal }) => generateAccessToken(params, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGenerateAccessTokenHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GenerateAccessTokenQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGenerateAccessTokenHook>>>
>;
export type GenerateAccessTokenQueryError =
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse;

export function useGenerateAccessToken<
  TData = Awaited<ReturnType<ReturnType<typeof useGenerateAccessTokenHook>>>,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(
  params: GenerateAccessTokenParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGenerateAccessTokenHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGenerateAccessTokenHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGenerateAccessTokenHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGenerateAccessToken<
  TData = Awaited<ReturnType<ReturnType<typeof useGenerateAccessTokenHook>>>,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(
  params: GenerateAccessTokenParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGenerateAccessTokenHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGenerateAccessTokenHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGenerateAccessTokenHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGenerateAccessToken<
  TData = Awaited<ReturnType<ReturnType<typeof useGenerateAccessTokenHook>>>,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(
  params: GenerateAccessTokenParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGenerateAccessTokenHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Generate an access token for a profile
 */

export function useGenerateAccessToken<
  TData = Awaited<ReturnType<ReturnType<typeof useGenerateAccessTokenHook>>>,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(
  params: GenerateAccessTokenParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGenerateAccessTokenHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useGenerateAccessTokenQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Get users current user profile
 */
export const useGetCurrentUserProfileHook = () => {
  const getCurrentUserProfile = useAxiosAuth<UserProfileDTO>();

  return useCallback(
    (signal?: AbortSignal) => {
      return getCurrentUserProfile({
        url: `/profiles/current`,
        method: 'GET',
        signal,
      });
    },
    [getCurrentUserProfile],
  );
};

export const getGetCurrentUserProfileQueryKey = () => {
  return [`/profiles/current`] as const;
};

export const useGetCurrentUserProfileQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetCurrentUserProfileHook>>>,
  TError = BadRequestResponse | UnauthorizedResponse | NotFoundResponse,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<ReturnType<typeof useGetCurrentUserProfileHook>>>,
      TError,
      TData
    >
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetCurrentUserProfileQueryKey();

  const getCurrentUserProfile = useGetCurrentUserProfileHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGetCurrentUserProfileHook>>>
  > = ({ signal }) => getCurrentUserProfile(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetCurrentUserProfileHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetCurrentUserProfileQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetCurrentUserProfileHook>>>
>;
export type GetCurrentUserProfileQueryError =
  | BadRequestResponse
  | UnauthorizedResponse
  | NotFoundResponse;

export function useGetCurrentUserProfile<
  TData = Awaited<ReturnType<ReturnType<typeof useGetCurrentUserProfileHook>>>,
  TError = BadRequestResponse | UnauthorizedResponse | NotFoundResponse,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetCurrentUserProfileHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetCurrentUserProfileHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetCurrentUserProfileHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetCurrentUserProfile<
  TData = Awaited<ReturnType<ReturnType<typeof useGetCurrentUserProfileHook>>>,
  TError = BadRequestResponse | UnauthorizedResponse | NotFoundResponse,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetCurrentUserProfileHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetCurrentUserProfileHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetCurrentUserProfileHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetCurrentUserProfile<
  TData = Awaited<ReturnType<ReturnType<typeof useGetCurrentUserProfileHook>>>,
  TError = BadRequestResponse | UnauthorizedResponse | NotFoundResponse,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetCurrentUserProfileHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get users current user profile
 */

export function useGetCurrentUserProfile<
  TData = Awaited<ReturnType<ReturnType<typeof useGetCurrentUserProfileHook>>>,
  TError = BadRequestResponse | UnauthorizedResponse | NotFoundResponse,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetCurrentUserProfileHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useGetCurrentUserProfileQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Readable with the Kratos session cookie when the session's email has a
user profile in the tenant, or a profile that can be issued a token whose
role grants `tenants:read` on this API. 404 when the tenant has no icon,
does not exist, is deleted or the caller may not read it.

 * @summary Get a tenant's icon
 */
export const useGetTenantIconHook = () => {
  const getTenantIcon = useAxiosAuth<IconResponse>();

  return useCallback(
    (
      tenantId: TenantIDDTO,
      params?: GetTenantIconParams,
      signal?: AbortSignal,
    ) => {
      return getTenantIcon({
        url: `/tenants/${tenantId}/icon`,
        method: 'GET',
        params,
        signal,
      });
    },
    [getTenantIcon],
  );
};

export const getGetTenantIconQueryKey = (
  tenantId?: TenantIDDTO,
  params?: GetTenantIconParams,
) => {
  return [`/tenants/${tenantId}/icon`, ...(params ? [params] : [])] as const;
};

export const useGetTenantIconQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetTenantIconHook>>>,
  TError = BadRequestResponse | UnauthorizedResponse | NotFoundResponse,
>(
  tenantId: TenantIDDTO,
  params?: GetTenantIconParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTenantIconHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetTenantIconQueryKey(tenantId, params);

  const getTenantIcon = useGetTenantIconHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGetTenantIconHook>>>
  > = ({ signal }) => getTenantIcon(tenantId, params, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!tenantId,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetTenantIconHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetTenantIconQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetTenantIconHook>>>
>;
export type GetTenantIconQueryError =
  | BadRequestResponse
  | UnauthorizedResponse
  | NotFoundResponse;

export function useGetTenantIcon<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTenantIconHook>>>,
  TError = BadRequestResponse | UnauthorizedResponse | NotFoundResponse,
>(
  tenantId: TenantIDDTO,
  params: undefined | GetTenantIconParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTenantIconHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetTenantIconHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetTenantIconHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetTenantIcon<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTenantIconHook>>>,
  TError = BadRequestResponse | UnauthorizedResponse | NotFoundResponse,
>(
  tenantId: TenantIDDTO,
  params?: GetTenantIconParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTenantIconHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetTenantIconHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetTenantIconHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetTenantIcon<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTenantIconHook>>>,
  TError = BadRequestResponse | UnauthorizedResponse | NotFoundResponse,
>(
  tenantId: TenantIDDTO,
  params?: GetTenantIconParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTenantIconHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get a tenant's icon
 */

export function useGetTenantIcon<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTenantIconHook>>>,
  TError = BadRequestResponse | UnauthorizedResponse | NotFoundResponse,
>(
  tenantId: TenantIDDTO,
  params?: GetTenantIconParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTenantIconHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useGetTenantIconQueryOptions(tenantId, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Readable with the Kratos session cookie when the session's email has a
user profile in a tenant the application is enabled for, or a profile
that can be issued a token whose role grants `applications:read` on this
API. 404 when the application has no icon, does not exist or the caller
may not read it.

 * @summary Get an application's icon
 */
export const useGetApplicationIconHook = () => {
  const getApplicationIcon = useAxiosAuth<IconResponse>();

  return useCallback(
    (
      applicationId: string,
      params?: GetApplicationIconParams,
      signal?: AbortSignal,
    ) => {
      return getApplicationIcon({
        url: `/applications/${applicationId}/icon`,
        method: 'GET',
        params,
        signal,
      });
    },
    [getApplicationIcon],
  );
};

export const getGetApplicationIconQueryKey = (
  applicationId?: string,
  params?: GetApplicationIconParams,
) => {
  return [
    `/applications/${applicationId}/icon`,
    ...(params ? [params] : []),
  ] as const;
};

export const useGetApplicationIconQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetApplicationIconHook>>>,
  TError = BadRequestResponse | UnauthorizedResponse | NotFoundResponse,
>(
  applicationId: string,
  params?: GetApplicationIconParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetApplicationIconHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetApplicationIconQueryKey(applicationId, params);

  const getApplicationIcon = useGetApplicationIconHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGetApplicationIconHook>>>
  > = ({ signal }) => getApplicationIcon(applicationId, params, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!applicationId,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetApplicationIconHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetApplicationIconQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetApplicationIconHook>>>
>;
export type GetApplicationIconQueryError =
  | BadRequestResponse
  | UnauthorizedResponse
  | NotFoundResponse;

export function useGetApplicationIcon<
  TData = Awaited<ReturnType<ReturnType<typeof useGetApplicationIconHook>>>,
  TError = BadRequestResponse | UnauthorizedResponse | NotFoundResponse,
>(
  applicationId: string,
  params: undefined | GetApplicationIconParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetApplicationIconHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetApplicationIconHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetApplicationIconHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetApplicationIcon<
  TData = Awaited<ReturnType<ReturnType<typeof useGetApplicationIconHook>>>,
  TError = BadRequestResponse | UnauthorizedResponse | NotFoundResponse,
>(
  applicationId: string,
  params?: GetApplicationIconParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetApplicationIconHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetApplicationIconHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetApplicationIconHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetApplicationIcon<
  TData = Awaited<ReturnType<ReturnType<typeof useGetApplicationIconHook>>>,
  TError = BadRequestResponse | UnauthorizedResponse | NotFoundResponse,
>(
  applicationId: string,
  params?: GetApplicationIconParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetApplicationIconHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get an application's icon
 */

export function useGetApplicationIcon<
  TData = Awaited<ReturnType<ReturnType<typeof useGetApplicationIconHook>>>,
  TError = BadRequestResponse | UnauthorizedResponse | NotFoundResponse,
>(
  applicationId: string,
  params?: GetApplicationIconParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetApplicationIconHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useGetApplicationIconQueryOptions(
    applicationId,
    params,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Create a new tenant
 */
export const useCreateTenantHook = () => {
  const createTenant = useAxiosAuth<TenantDTO>();

  return useCallback(
    (createTenantRequestDTO: CreateTenantRequestDTO, signal?: AbortSignal) => {
      return createTenant({
        url: `/admin/tenants`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: createTenantRequestDTO,
        signal,
      });
    },
    [createTenant],
  );
};

export const useCreateTenantMutationOptions = <
  TError = BadRequestResponse | UnauthorizedResponse | ForbiddenResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useCreateTenantHook>>>,
    TError,
    { data: CreateTenantRequestDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useCreateTenantHook>>>,
  TError,
  { data: CreateTenantRequestDTO },
  TContext
> => {
  const mutationKey = ['createTenant'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const createTenant = useCreateTenantHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useCreateTenantHook>>>,
    { data: CreateTenantRequestDTO }
  > = (props) => {
    const { data } = props ?? {};

    return createTenant(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateTenantMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useCreateTenantHook>>>
>;
export type CreateTenantMutationBody = CreateTenantRequestDTO;
export type CreateTenantMutationError =
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse;

/**
 * @summary Create a new tenant
 */
export const useCreateTenant = <
  TError = BadRequestResponse | UnauthorizedResponse | ForbiddenResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useCreateTenantHook>>>,
      TError,
      { data: CreateTenantRequestDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useCreateTenantHook>>>,
  TError,
  { data: CreateTenantRequestDTO },
  TContext
> => {
  const mutationOptions = useCreateTenantMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * @summary List all tenants
 */
export const useListTenantsHook = () => {
  const listTenants = useAxiosAuth<TenantDTO[]>();

  return useCallback(
    (signal?: AbortSignal) => {
      return listTenants({ url: `/admin/tenants`, method: 'GET', signal });
    },
    [listTenants],
  );
};

export const getListTenantsQueryKey = () => {
  return [`/admin/tenants`] as const;
};

export const useListTenantsQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useListTenantsHook>>>,
  TError = UnauthorizedResponse | ForbiddenResponse,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<ReturnType<typeof useListTenantsHook>>>,
      TError,
      TData
    >
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListTenantsQueryKey();

  const listTenants = useListTenantsHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useListTenantsHook>>>
  > = ({ signal }) => listTenants(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useListTenantsHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type ListTenantsQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useListTenantsHook>>>
>;
export type ListTenantsQueryError = UnauthorizedResponse | ForbiddenResponse;

export function useListTenants<
  TData = Awaited<ReturnType<ReturnType<typeof useListTenantsHook>>>,
  TError = UnauthorizedResponse | ForbiddenResponse,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListTenantsHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useListTenantsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useListTenantsHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useListTenants<
  TData = Awaited<ReturnType<ReturnType<typeof useListTenantsHook>>>,
  TError = UnauthorizedResponse | ForbiddenResponse,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListTenantsHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useListTenantsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useListTenantsHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useListTenants<
  TData = Awaited<ReturnType<ReturnType<typeof useListTenantsHook>>>,
  TError = UnauthorizedResponse | ForbiddenResponse,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListTenantsHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary List all tenants
 */

export function useListTenants<
  TData = Awaited<ReturnType<ReturnType<typeof useListTenantsHook>>>,
  TError = UnauthorizedResponse | ForbiddenResponse,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListTenantsHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useListTenantsQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Get a specific tenant
 */
export const useGetTenantHook = () => {
  const getTenant = useAxiosAuth<TenantDTO>();

  return useCallback(
    (tenantId: TenantIDDTO, signal?: AbortSignal) => {
      return getTenant({
        url: `/admin/tenants/${tenantId}`,
        method: 'GET',
        signal,
      });
    },
    [getTenant],
  );
};

export const getGetTenantQueryKey = (tenantId?: TenantIDDTO) => {
  return [`/admin/tenants/${tenantId}`] as const;
};

export const useGetTenantQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetTenantHook>>>,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(
  tenantId: TenantIDDTO,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTenantHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetTenantQueryKey(tenantId);

  const getTenant = useGetTenantHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGetTenantHook>>>
  > = ({ signal }) => getTenant(tenantId, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!tenantId,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetTenantHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetTenantQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetTenantHook>>>
>;
export type GetTenantQueryError =
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse;

export function useGetTenant<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTenantHook>>>,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(
  tenantId: TenantIDDTO,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTenantHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetTenantHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetTenantHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetTenant<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTenantHook>>>,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(
  tenantId: TenantIDDTO,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTenantHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetTenantHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetTenantHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetTenant<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTenantHook>>>,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(
  tenantId: TenantIDDTO,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTenantHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get a specific tenant
 */

export function useGetTenant<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTenantHook>>>,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(
  tenantId: TenantIDDTO,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTenantHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useGetTenantQueryOptions(tenantId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Update a tenant
 */
export const useUpdateTenantHook = () => {
  const updateTenant = useAxiosAuth<TenantDTO>();

  return useCallback(
    (tenantId: TenantIDDTO, updateTenantRequestDTO: UpdateTenantRequestDTO) => {
      return updateTenant({
        url: `/admin/tenants/${tenantId}`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        data: updateTenantRequestDTO,
      });
    },
    [updateTenant],
  );
};

export const useUpdateTenantMutationOptions = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdateTenantHook>>>,
    TError,
    { tenantId: TenantIDDTO; data: UpdateTenantRequestDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useUpdateTenantHook>>>,
  TError,
  { tenantId: TenantIDDTO; data: UpdateTenantRequestDTO },
  TContext
> => {
  const mutationKey = ['updateTenant'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const updateTenant = useUpdateTenantHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useUpdateTenantHook>>>,
    { tenantId: TenantIDDTO; data: UpdateTenantRequestDTO }
  > = (props) => {
    const { tenantId, data } = props ?? {};

    return updateTenant(tenantId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateTenantMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useUpdateTenantHook>>>
>;
export type UpdateTenantMutationBody = UpdateTenantRequestDTO;
export type UpdateTenantMutationError =
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse;

/**
 * @summary Update a tenant
 */
export const useUpdateTenant = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useUpdateTenantHook>>>,
      TError,
      { tenantId: TenantIDDTO; data: UpdateTenantRequestDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useUpdateTenantHook>>>,
  TError,
  { tenantId: TenantIDDTO; data: UpdateTenantRequestDTO },
  TContext
> => {
  const mutationOptions = useUpdateTenantMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Also removes the tenant's icon, so the response has no `icon_updated_at`.
 * @summary Delete a tenant
 */
export const useDeleteTenantHook = () => {
  const deleteTenant = useAxiosAuth<TenantDTO>();

  return useCallback(
    (tenantId: TenantIDDTO) => {
      return deleteTenant({
        url: `/admin/tenants/${tenantId}`,
        method: 'DELETE',
      });
    },
    [deleteTenant],
  );
};

export const useDeleteTenantMutationOptions = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteTenantHook>>>,
    TError,
    { tenantId: TenantIDDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useDeleteTenantHook>>>,
  TError,
  { tenantId: TenantIDDTO },
  TContext
> => {
  const mutationKey = ['deleteTenant'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const deleteTenant = useDeleteTenantHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useDeleteTenantHook>>>,
    { tenantId: TenantIDDTO }
  > = (props) => {
    const { tenantId } = props ?? {};

    return deleteTenant(tenantId);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteTenantMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useDeleteTenantHook>>>
>;

export type DeleteTenantMutationError =
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse;

/**
 * @summary Delete a tenant
 */
export const useDeleteTenant = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useDeleteTenantHook>>>,
      TError,
      { tenantId: TenantIDDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useDeleteTenantHook>>>,
  TError,
  { tenantId: TenantIDDTO },
  TContext
> => {
  const mutationOptions = useDeleteTenantMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * @summary Create a new user profile for a tenant
 */
export const useCreateTenantUserProfileHook = () => {
  const createTenantUserProfile = useAxiosAuth<UserProfileDTO>();

  return useCallback(
    (
      tenantId: TenantIDDTO,
      createTenantUserProfileRequestDTO: CreateTenantUserProfileRequestDTO,
      signal?: AbortSignal,
    ) => {
      return createTenantUserProfile({
        url: `/admin/tenants/${tenantId}/userprofiles`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: createTenantUserProfileRequestDTO,
        signal,
      });
    },
    [createTenantUserProfile],
  );
};

export const useCreateTenantUserProfileMutationOptions = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UserProfileConflictResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useCreateTenantUserProfileHook>>>,
    TError,
    { tenantId: TenantIDDTO; data: CreateTenantUserProfileRequestDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useCreateTenantUserProfileHook>>>,
  TError,
  { tenantId: TenantIDDTO; data: CreateTenantUserProfileRequestDTO },
  TContext
> => {
  const mutationKey = ['createTenantUserProfile'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const createTenantUserProfile = useCreateTenantUserProfileHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useCreateTenantUserProfileHook>>>,
    { tenantId: TenantIDDTO; data: CreateTenantUserProfileRequestDTO }
  > = (props) => {
    const { tenantId, data } = props ?? {};

    return createTenantUserProfile(tenantId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateTenantUserProfileMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useCreateTenantUserProfileHook>>>
>;
export type CreateTenantUserProfileMutationBody =
  CreateTenantUserProfileRequestDTO;
export type CreateTenantUserProfileMutationError =
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
  | UserProfileConflictResponse;

/**
 * @summary Create a new user profile for a tenant
 */
export const useCreateTenantUserProfile = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UserProfileConflictResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useCreateTenantUserProfileHook>>>,
      TError,
      { tenantId: TenantIDDTO; data: CreateTenantUserProfileRequestDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useCreateTenantUserProfileHook>>>,
  TError,
  { tenantId: TenantIDDTO; data: CreateTenantUserProfileRequestDTO },
  TContext
> => {
  const mutationOptions = useCreateTenantUserProfileMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Lists all user profiles for the tenant.
 * @summary List user profiles for a tenant
 */
export const useListTenantUserProfilesHook = () => {
  const listTenantUserProfiles = useAxiosAuth<UserProfileDTO[]>();

  return useCallback(
    (tenantId: TenantIDDTO, signal?: AbortSignal) => {
      return listTenantUserProfiles({
        url: `/admin/tenants/${tenantId}/userprofiles`,
        method: 'GET',
        signal,
      });
    },
    [listTenantUserProfiles],
  );
};

export const getListTenantUserProfilesQueryKey = (tenantId?: TenantIDDTO) => {
  return [`/admin/tenants/${tenantId}/userprofiles`] as const;
};

export const useListTenantUserProfilesQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useListTenantUserProfilesHook>>>,
  TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse,
>(
  tenantId: TenantIDDTO,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListTenantUserProfilesHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListTenantUserProfilesQueryKey(tenantId);

  const listTenantUserProfiles = useListTenantUserProfilesHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useListTenantUserProfilesHook>>>
  > = ({ signal }) => listTenantUserProfiles(tenantId, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!tenantId,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useListTenantUserProfilesHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type ListTenantUserProfilesQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useListTenantUserProfilesHook>>>
>;
export type ListTenantUserProfilesQueryError =
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse;

export function useListTenantUserProfiles<
  TData = Awaited<ReturnType<ReturnType<typeof useListTenantUserProfilesHook>>>,
  TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse,
>(
  tenantId: TenantIDDTO,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListTenantUserProfilesHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useListTenantUserProfilesHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useListTenantUserProfilesHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useListTenantUserProfiles<
  TData = Awaited<ReturnType<ReturnType<typeof useListTenantUserProfilesHook>>>,
  TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse,
>(
  tenantId: TenantIDDTO,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListTenantUserProfilesHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useListTenantUserProfilesHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useListTenantUserProfilesHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useListTenantUserProfiles<
  TData = Awaited<ReturnType<ReturnType<typeof useListTenantUserProfilesHook>>>,
  TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse,
>(
  tenantId: TenantIDDTO,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListTenantUserProfilesHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary List user profiles for a tenant
 */

export function useListTenantUserProfiles<
  TData = Awaited<ReturnType<ReturnType<typeof useListTenantUserProfilesHook>>>,
  TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse,
>(
  tenantId: TenantIDDTO,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListTenantUserProfilesHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useListTenantUserProfilesQueryOptions(tenantId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Get a specific user profile for a tenant. With an optional organization_id to filter on.

 * @summary Get a specific user profile for a tenant
 */
export const useGetTenantUserProfileHook = () => {
  const getTenantUserProfile = useAxiosAuth<UserProfileDTO>();

  return useCallback(
    (
      tenantId: TenantIDDTO,
      userProfileId: string,
      params?: GetTenantUserProfileParams,
      signal?: AbortSignal,
    ) => {
      return getTenantUserProfile({
        url: `/admin/tenants/${tenantId}/userprofiles/${userProfileId}`,
        method: 'GET',
        params,
        signal,
      });
    },
    [getTenantUserProfile],
  );
};

export const getGetTenantUserProfileQueryKey = (
  tenantId?: TenantIDDTO,
  userProfileId?: string,
  params?: GetTenantUserProfileParams,
) => {
  return [
    `/admin/tenants/${tenantId}/userprofiles/${userProfileId}`,
    ...(params ? [params] : []),
  ] as const;
};

export const useGetTenantUserProfileQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetTenantUserProfileHook>>>,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(
  tenantId: TenantIDDTO,
  userProfileId: string,
  params?: GetTenantUserProfileParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTenantUserProfileHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetTenantUserProfileQueryKey(tenantId, userProfileId, params);

  const getTenantUserProfile = useGetTenantUserProfileHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGetTenantUserProfileHook>>>
  > = ({ signal }) =>
    getTenantUserProfile(tenantId, userProfileId, params, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!(tenantId && userProfileId),
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetTenantUserProfileHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetTenantUserProfileQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetTenantUserProfileHook>>>
>;
export type GetTenantUserProfileQueryError =
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse;

export function useGetTenantUserProfile<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTenantUserProfileHook>>>,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(
  tenantId: TenantIDDTO,
  userProfileId: string,
  params: undefined | GetTenantUserProfileParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTenantUserProfileHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetTenantUserProfileHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetTenantUserProfileHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetTenantUserProfile<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTenantUserProfileHook>>>,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(
  tenantId: TenantIDDTO,
  userProfileId: string,
  params?: GetTenantUserProfileParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTenantUserProfileHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetTenantUserProfileHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetTenantUserProfileHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetTenantUserProfile<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTenantUserProfileHook>>>,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(
  tenantId: TenantIDDTO,
  userProfileId: string,
  params?: GetTenantUserProfileParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTenantUserProfileHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get a specific user profile for a tenant
 */

export function useGetTenantUserProfile<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTenantUserProfileHook>>>,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(
  tenantId: TenantIDDTO,
  userProfileId: string,
  params?: GetTenantUserProfileParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTenantUserProfileHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useGetTenantUserProfileQueryOptions(
    tenantId,
    userProfileId,
    params,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Update a tenant user profile
 */
export const useUpdateTenantUserProfileHook = () => {
  const updateTenantUserProfile = useAxiosAuth<UserProfileDTO>();

  return useCallback(
    (
      tenantId: TenantIDDTO,
      userProfileId: string,
      updateUserProfileRequestDTO: UpdateUserProfileRequestDTO,
    ) => {
      return updateTenantUserProfile({
        url: `/admin/tenants/${tenantId}/userprofiles/${userProfileId}`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        data: updateUserProfileRequestDTO,
      });
    },
    [updateTenantUserProfile],
  );
};

export const useUpdateTenantUserProfileMutationOptions = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdateTenantUserProfileHook>>>,
    TError,
    {
      tenantId: TenantIDDTO;
      userProfileId: string;
      data: UpdateUserProfileRequestDTO;
    },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useUpdateTenantUserProfileHook>>>,
  TError,
  {
    tenantId: TenantIDDTO;
    userProfileId: string;
    data: UpdateUserProfileRequestDTO;
  },
  TContext
> => {
  const mutationKey = ['updateTenantUserProfile'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const updateTenantUserProfile = useUpdateTenantUserProfileHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useUpdateTenantUserProfileHook>>>,
    {
      tenantId: TenantIDDTO;
      userProfileId: string;
      data: UpdateUserProfileRequestDTO;
    }
  > = (props) => {
    const { tenantId, userProfileId, data } = props ?? {};

    return updateTenantUserProfile(tenantId, userProfileId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateTenantUserProfileMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useUpdateTenantUserProfileHook>>>
>;
export type UpdateTenantUserProfileMutationBody = UpdateUserProfileRequestDTO;
export type UpdateTenantUserProfileMutationError =
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse;

/**
 * @summary Update a tenant user profile
 */
export const useUpdateTenantUserProfile = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useUpdateTenantUserProfileHook>>>,
      TError,
      {
        tenantId: TenantIDDTO;
        userProfileId: string;
        data: UpdateUserProfileRequestDTO;
      },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useUpdateTenantUserProfileHook>>>,
  TError,
  {
    tenantId: TenantIDDTO;
    userProfileId: string;
    data: UpdateUserProfileRequestDTO;
  },
  TContext
> => {
  const mutationOptions = useUpdateTenantUserProfileMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * @summary Delete a user profile for a tenant
 */
export const useDeleteTenantUserProfileHook = () => {
  const deleteTenantUserProfile = useAxiosAuth<UserProfileDTO>();

  return useCallback(
    (tenantId: TenantIDDTO, userProfileId: string) => {
      return deleteTenantUserProfile({
        url: `/admin/tenants/${tenantId}/userprofiles/${userProfileId}`,
        method: 'DELETE',
      });
    },
    [deleteTenantUserProfile],
  );
};

export const useDeleteTenantUserProfileMutationOptions = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteTenantUserProfileHook>>>,
    TError,
    { tenantId: TenantIDDTO; userProfileId: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useDeleteTenantUserProfileHook>>>,
  TError,
  { tenantId: TenantIDDTO; userProfileId: string },
  TContext
> => {
  const mutationKey = ['deleteTenantUserProfile'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const deleteTenantUserProfile = useDeleteTenantUserProfileHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useDeleteTenantUserProfileHook>>>,
    { tenantId: TenantIDDTO; userProfileId: string }
  > = (props) => {
    const { tenantId, userProfileId } = props ?? {};

    return deleteTenantUserProfile(tenantId, userProfileId);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteTenantUserProfileMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useDeleteTenantUserProfileHook>>>
>;

export type DeleteTenantUserProfileMutationError =
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse;

/**
 * @summary Delete a user profile for a tenant
 */
export const useDeleteTenantUserProfile = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useDeleteTenantUserProfileHook>>>,
      TError,
      { tenantId: TenantIDDTO; userProfileId: string },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useDeleteTenantUserProfileHook>>>,
  TError,
  { tenantId: TenantIDDTO; userProfileId: string },
  TContext
> => {
  const mutationOptions = useDeleteTenantUserProfileMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Lists all organizations for all tenants.

 * @summary List all organizations
 */
export const useListAllTenantOrganizationsHook = () => {
  const listAllTenantOrganizations = useAxiosAuth<OrganizationDTO[]>();

  return useCallback(
    (signal?: AbortSignal) => {
      return listAllTenantOrganizations({
        url: `/admin/organizations`,
        method: 'GET',
        signal,
      });
    },
    [listAllTenantOrganizations],
  );
};

export const getListAllTenantOrganizationsQueryKey = () => {
  return [`/admin/organizations`] as const;
};

export const useListAllTenantOrganizationsQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useListAllTenantOrganizationsHook>>
  >,
  TError = BadRequestResponse | UnauthorizedResponse | ForbiddenResponse,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<ReturnType<typeof useListAllTenantOrganizationsHook>>>,
      TError,
      TData
    >
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListAllTenantOrganizationsQueryKey();

  const listAllTenantOrganizations = useListAllTenantOrganizationsHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useListAllTenantOrganizationsHook>>>
  > = ({ signal }) => listAllTenantOrganizations(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useListAllTenantOrganizationsHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type ListAllTenantOrganizationsQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useListAllTenantOrganizationsHook>>>
>;
export type ListAllTenantOrganizationsQueryError =
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse;

export function useListAllTenantOrganizations<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListAllTenantOrganizationsHook>>
  >,
  TError = BadRequestResponse | UnauthorizedResponse | ForbiddenResponse,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useListAllTenantOrganizationsHook>>
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useListAllTenantOrganizationsHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useListAllTenantOrganizationsHook>>
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useListAllTenantOrganizations<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListAllTenantOrganizationsHook>>
  >,
  TError = BadRequestResponse | UnauthorizedResponse | ForbiddenResponse,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useListAllTenantOrganizationsHook>>
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useListAllTenantOrganizationsHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useListAllTenantOrganizationsHook>>
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useListAllTenantOrganizations<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListAllTenantOrganizationsHook>>
  >,
  TError = BadRequestResponse | UnauthorizedResponse | ForbiddenResponse,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useListAllTenantOrganizationsHook>>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary List all organizations
 */

export function useListAllTenantOrganizations<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListAllTenantOrganizationsHook>>
  >,
  TError = BadRequestResponse | UnauthorizedResponse | ForbiddenResponse,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useListAllTenantOrganizationsHook>>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useListAllTenantOrganizationsQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Create a new organization for tenant.
The organization is linked to application_id: 404 if the application
does not exist or is not enabled for the tenant, 400 if the tenant
application is inactive or the application has organization_management
`disabled`. `external` applications are accepted on this route.

 * @summary Create a new organization for tenant
 */
export const useCreateTenantOrganizationHook = () => {
  const createTenantOrganization = useAxiosAuth<OrganizationDTO>();

  return useCallback(
    (
      tenantId: TenantIDDTO,
      createTenantOrganizationRequestDTO: CreateTenantOrganizationRequestDTO,
      signal?: AbortSignal,
    ) => {
      return createTenantOrganization({
        url: `/admin/tenants/${tenantId}/organizations`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: createTenantOrganizationRequestDTO,
        signal,
      });
    },
    [createTenantOrganization],
  );
};

export const useCreateTenantOrganizationMutationOptions = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useCreateTenantOrganizationHook>>>,
    TError,
    { tenantId: TenantIDDTO; data: CreateTenantOrganizationRequestDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useCreateTenantOrganizationHook>>>,
  TError,
  { tenantId: TenantIDDTO; data: CreateTenantOrganizationRequestDTO },
  TContext
> => {
  const mutationKey = ['createTenantOrganization'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const createTenantOrganization = useCreateTenantOrganizationHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useCreateTenantOrganizationHook>>>,
    { tenantId: TenantIDDTO; data: CreateTenantOrganizationRequestDTO }
  > = (props) => {
    const { tenantId, data } = props ?? {};

    return createTenantOrganization(tenantId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateTenantOrganizationMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useCreateTenantOrganizationHook>>>
>;
export type CreateTenantOrganizationMutationBody =
  CreateTenantOrganizationRequestDTO;
export type CreateTenantOrganizationMutationError =
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse;

/**
 * @summary Create a new organization for tenant
 */
export const useCreateTenantOrganization = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useCreateTenantOrganizationHook>>>,
      TError,
      { tenantId: TenantIDDTO; data: CreateTenantOrganizationRequestDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useCreateTenantOrganizationHook>>>,
  TError,
  { tenantId: TenantIDDTO; data: CreateTenantOrganizationRequestDTO },
  TContext
> => {
  const mutationOptions = useCreateTenantOrganizationMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Lists all organizations for tenant.

 * @summary List organizations for tenant
 */
export const useListTenantOrganizationsHook = () => {
  const listTenantOrganizations = useAxiosAuth<OrganizationDTO[]>();

  return useCallback(
    (tenantId: TenantIDDTO, signal?: AbortSignal) => {
      return listTenantOrganizations({
        url: `/admin/tenants/${tenantId}/organizations`,
        method: 'GET',
        signal,
      });
    },
    [listTenantOrganizations],
  );
};

export const getListTenantOrganizationsQueryKey = (tenantId?: TenantIDDTO) => {
  return [`/admin/tenants/${tenantId}/organizations`] as const;
};

export const useListTenantOrganizationsQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useListTenantOrganizationsHook>>
  >,
  TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse,
>(
  tenantId: TenantIDDTO,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListTenantOrganizationsHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListTenantOrganizationsQueryKey(tenantId);

  const listTenantOrganizations = useListTenantOrganizationsHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useListTenantOrganizationsHook>>>
  > = ({ signal }) => listTenantOrganizations(tenantId, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!tenantId,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useListTenantOrganizationsHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type ListTenantOrganizationsQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useListTenantOrganizationsHook>>>
>;
export type ListTenantOrganizationsQueryError =
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse;

export function useListTenantOrganizations<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListTenantOrganizationsHook>>
  >,
  TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse,
>(
  tenantId: TenantIDDTO,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListTenantOrganizationsHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useListTenantOrganizationsHook>>
          >,
          TError,
          Awaited<ReturnType<ReturnType<typeof useListTenantOrganizationsHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useListTenantOrganizations<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListTenantOrganizationsHook>>
  >,
  TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse,
>(
  tenantId: TenantIDDTO,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListTenantOrganizationsHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useListTenantOrganizationsHook>>
          >,
          TError,
          Awaited<ReturnType<ReturnType<typeof useListTenantOrganizationsHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useListTenantOrganizations<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListTenantOrganizationsHook>>
  >,
  TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse,
>(
  tenantId: TenantIDDTO,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListTenantOrganizationsHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary List organizations for tenant
 */

export function useListTenantOrganizations<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListTenantOrganizationsHook>>
  >,
  TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse,
>(
  tenantId: TenantIDDTO,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListTenantOrganizationsHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useListTenantOrganizationsQueryOptions(
    tenantId,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Get a specific organization for tenant.

 * @summary Get organization for tenant
 */
export const useGetTenantOrganizationHook = () => {
  const getTenantOrganization = useAxiosAuth<OrganizationDTO>();

  return useCallback(
    (tenantId: TenantIDDTO, organizationId: string, signal?: AbortSignal) => {
      return getTenantOrganization({
        url: `/admin/tenants/${tenantId}/organizations/${organizationId}`,
        method: 'GET',
        signal,
      });
    },
    [getTenantOrganization],
  );
};

export const getGetTenantOrganizationQueryKey = (
  tenantId?: TenantIDDTO,
  organizationId?: string,
) => {
  return [
    `/admin/tenants/${tenantId}/organizations/${organizationId}`,
  ] as const;
};

export const useGetTenantOrganizationQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetTenantOrganizationHook>>>,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(
  tenantId: TenantIDDTO,
  organizationId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTenantOrganizationHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetTenantOrganizationQueryKey(tenantId, organizationId);

  const getTenantOrganization = useGetTenantOrganizationHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGetTenantOrganizationHook>>>
  > = ({ signal }) => getTenantOrganization(tenantId, organizationId, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!(tenantId && organizationId),
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetTenantOrganizationHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetTenantOrganizationQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetTenantOrganizationHook>>>
>;
export type GetTenantOrganizationQueryError =
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse;

export function useGetTenantOrganization<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTenantOrganizationHook>>>,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(
  tenantId: TenantIDDTO,
  organizationId: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTenantOrganizationHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetTenantOrganizationHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetTenantOrganizationHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetTenantOrganization<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTenantOrganizationHook>>>,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(
  tenantId: TenantIDDTO,
  organizationId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTenantOrganizationHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetTenantOrganizationHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetTenantOrganizationHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetTenantOrganization<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTenantOrganizationHook>>>,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(
  tenantId: TenantIDDTO,
  organizationId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTenantOrganizationHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get organization for tenant
 */

export function useGetTenantOrganization<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTenantOrganizationHook>>>,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(
  tenantId: TenantIDDTO,
  organizationId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTenantOrganizationHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useGetTenantOrganizationQueryOptions(
    tenantId,
    organizationId,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Update organization for tenant
 */
export const useUpdateTenantOrganizationHook = () => {
  const updateTenantOrganization = useAxiosAuth<OrganizationDTO>();

  return useCallback(
    (
      tenantId: TenantIDDTO,
      organizationId: string,
      updateOrganizationRequestDTO: UpdateOrganizationRequestDTO,
    ) => {
      return updateTenantOrganization({
        url: `/admin/tenants/${tenantId}/organizations/${organizationId}`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        data: updateOrganizationRequestDTO,
      });
    },
    [updateTenantOrganization],
  );
};

export const useUpdateTenantOrganizationMutationOptions = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdateTenantOrganizationHook>>>,
    TError,
    {
      tenantId: TenantIDDTO;
      organizationId: string;
      data: UpdateOrganizationRequestDTO;
    },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useUpdateTenantOrganizationHook>>>,
  TError,
  {
    tenantId: TenantIDDTO;
    organizationId: string;
    data: UpdateOrganizationRequestDTO;
  },
  TContext
> => {
  const mutationKey = ['updateTenantOrganization'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const updateTenantOrganization = useUpdateTenantOrganizationHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useUpdateTenantOrganizationHook>>>,
    {
      tenantId: TenantIDDTO;
      organizationId: string;
      data: UpdateOrganizationRequestDTO;
    }
  > = (props) => {
    const { tenantId, organizationId, data } = props ?? {};

    return updateTenantOrganization(tenantId, organizationId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateTenantOrganizationMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useUpdateTenantOrganizationHook>>>
>;
export type UpdateTenantOrganizationMutationBody = UpdateOrganizationRequestDTO;
export type UpdateTenantOrganizationMutationError =
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse;

/**
 * @summary Update organization for tenant
 */
export const useUpdateTenantOrganization = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useUpdateTenantOrganizationHook>>>,
      TError,
      {
        tenantId: TenantIDDTO;
        organizationId: string;
        data: UpdateOrganizationRequestDTO;
      },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useUpdateTenantOrganizationHook>>>,
  TError,
  {
    tenantId: TenantIDDTO;
    organizationId: string;
    data: UpdateOrganizationRequestDTO;
  },
  TContext
> => {
  const mutationOptions = useUpdateTenantOrganizationMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * @summary Delete organization for tenant
 */
export const useDeleteTenantOrganizationHook = () => {
  const deleteTenantOrganization = useAxiosAuth<OrganizationDTO>();

  return useCallback(
    (tenantId: TenantIDDTO, organizationId: string) => {
      return deleteTenantOrganization({
        url: `/admin/tenants/${tenantId}/organizations/${organizationId}`,
        method: 'DELETE',
      });
    },
    [deleteTenantOrganization],
  );
};

export const useDeleteTenantOrganizationMutationOptions = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteTenantOrganizationHook>>>,
    TError,
    { tenantId: TenantIDDTO; organizationId: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useDeleteTenantOrganizationHook>>>,
  TError,
  { tenantId: TenantIDDTO; organizationId: string },
  TContext
> => {
  const mutationKey = ['deleteTenantOrganization'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const deleteTenantOrganization = useDeleteTenantOrganizationHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useDeleteTenantOrganizationHook>>>,
    { tenantId: TenantIDDTO; organizationId: string }
  > = (props) => {
    const { tenantId, organizationId } = props ?? {};

    return deleteTenantOrganization(tenantId, organizationId);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteTenantOrganizationMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useDeleteTenantOrganizationHook>>>
>;

export type DeleteTenantOrganizationMutationError =
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse;

/**
 * @summary Delete organization for tenant
 */
export const useDeleteTenantOrganization = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useDeleteTenantOrganizationHook>>>,
      TError,
      { tenantId: TenantIDDTO; organizationId: string },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useDeleteTenantOrganizationHook>>>,
  TError,
  { tenantId: TenantIDDTO; organizationId: string },
  TContext
> => {
  const mutationOptions = useDeleteTenantOrganizationMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Set the active flag for an organization. When inactive (active=false), the organization is treated as deleted and its resources are inaccessible.

 * @summary Activate or inactivate organization for tenant
 */
export const useUpdateTenantOrganizationActiveHook = () => {
  const updateTenantOrganizationActive = useAxiosAuth<OrganizationDTO>();

  return useCallback(
    (
      tenantId: TenantIDDTO,
      organizationId: string,
      updateOrganizationActiveRequestDTO: UpdateOrganizationActiveRequestDTO,
    ) => {
      return updateTenantOrganizationActive({
        url: `/admin/tenants/${tenantId}/organizations/${organizationId}`,
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        data: updateOrganizationActiveRequestDTO,
      });
    },
    [updateTenantOrganizationActive],
  );
};

export const useUpdateTenantOrganizationActiveMutationOptions = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<
      ReturnType<ReturnType<typeof useUpdateTenantOrganizationActiveHook>>
    >,
    TError,
    {
      tenantId: TenantIDDTO;
      organizationId: string;
      data: UpdateOrganizationActiveRequestDTO;
    },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useUpdateTenantOrganizationActiveHook>>>,
  TError,
  {
    tenantId: TenantIDDTO;
    organizationId: string;
    data: UpdateOrganizationActiveRequestDTO;
  },
  TContext
> => {
  const mutationKey = ['updateTenantOrganizationActive'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const updateTenantOrganizationActive =
    useUpdateTenantOrganizationActiveHook();

  const mutationFn: MutationFunction<
    Awaited<
      ReturnType<ReturnType<typeof useUpdateTenantOrganizationActiveHook>>
    >,
    {
      tenantId: TenantIDDTO;
      organizationId: string;
      data: UpdateOrganizationActiveRequestDTO;
    }
  > = (props) => {
    const { tenantId, organizationId, data } = props ?? {};

    return updateTenantOrganizationActive(tenantId, organizationId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateTenantOrganizationActiveMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useUpdateTenantOrganizationActiveHook>>>
>;
export type UpdateTenantOrganizationActiveMutationBody =
  UpdateOrganizationActiveRequestDTO;
export type UpdateTenantOrganizationActiveMutationError =
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse;

/**
 * @summary Activate or inactivate organization for tenant
 */
export const useUpdateTenantOrganizationActive = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<ReturnType<typeof useUpdateTenantOrganizationActiveHook>>
      >,
      TError,
      {
        tenantId: TenantIDDTO;
        organizationId: string;
        data: UpdateOrganizationActiveRequestDTO;
      },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useUpdateTenantOrganizationActiveHook>>>,
  TError,
  {
    tenantId: TenantIDDTO;
    organizationId: string;
    data: UpdateOrganizationActiveRequestDTO;
  },
  TContext
> => {
  const mutationOptions =
    useUpdateTenantOrganizationActiveMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * @summary List IdP-only domains owned by tenant
 */
export const useListTenantIDPOnlyDomainsHook = () => {
  const listTenantIDPOnlyDomains = useAxiosAuth<TenantIDPOnlyDomainDTO[]>();

  return useCallback(
    (tenantId: TenantIDDTO, signal?: AbortSignal) => {
      return listTenantIDPOnlyDomains({
        url: `/admin/tenants/${tenantId}/idp-only-domains`,
        method: 'GET',
        signal,
      });
    },
    [listTenantIDPOnlyDomains],
  );
};

export const getListTenantIDPOnlyDomainsQueryKey = (tenantId?: TenantIDDTO) => {
  return [`/admin/tenants/${tenantId}/idp-only-domains`] as const;
};

export const useListTenantIDPOnlyDomainsQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useListTenantIDPOnlyDomainsHook>>
  >,
  TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse,
>(
  tenantId: TenantIDDTO,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListTenantIDPOnlyDomainsHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListTenantIDPOnlyDomainsQueryKey(tenantId);

  const listTenantIDPOnlyDomains = useListTenantIDPOnlyDomainsHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useListTenantIDPOnlyDomainsHook>>>
  > = ({ signal }) => listTenantIDPOnlyDomains(tenantId, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!tenantId,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useListTenantIDPOnlyDomainsHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type ListTenantIDPOnlyDomainsQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useListTenantIDPOnlyDomainsHook>>>
>;
export type ListTenantIDPOnlyDomainsQueryError =
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse;

export function useListTenantIDPOnlyDomains<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListTenantIDPOnlyDomainsHook>>
  >,
  TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse,
>(
  tenantId: TenantIDDTO,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListTenantIDPOnlyDomainsHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useListTenantIDPOnlyDomainsHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useListTenantIDPOnlyDomainsHook>>
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useListTenantIDPOnlyDomains<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListTenantIDPOnlyDomainsHook>>
  >,
  TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse,
>(
  tenantId: TenantIDDTO,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListTenantIDPOnlyDomainsHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useListTenantIDPOnlyDomainsHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useListTenantIDPOnlyDomainsHook>>
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useListTenantIDPOnlyDomains<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListTenantIDPOnlyDomainsHook>>
  >,
  TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse,
>(
  tenantId: TenantIDDTO,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListTenantIDPOnlyDomainsHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary List IdP-only domains owned by tenant
 */

export function useListTenantIDPOnlyDomains<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListTenantIDPOnlyDomainsHook>>
  >,
  TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse,
>(
  tenantId: TenantIDDTO,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListTenantIDPOnlyDomainsHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useListTenantIDPOnlyDomainsQueryOptions(
    tenantId,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Idempotent: returns the existing domain when the tenant already owns it. The domain is trimmed
and lowercased and must be a hostname of at least two labels (ASCII letters, digits and hyphens;
internationalised names in their xn-- form), like `example.se`. 409 if another tenant owns the domain.

 * @summary Add an IdP-only domain to tenant
 */
export const usePutTenantIDPOnlyDomainHook = () => {
  const putTenantIDPOnlyDomain = useAxiosAuth<TenantIDPOnlyDomainDTO>();

  return useCallback(
    (tenantId: TenantIDDTO, domain: string) => {
      return putTenantIDPOnlyDomain({
        url: `/admin/tenants/${tenantId}/idp-only-domains/${domain}`,
        method: 'PUT',
      });
    },
    [putTenantIDPOnlyDomain],
  );
};

export const usePutTenantIDPOnlyDomainMutationOptions = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | ConflictResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof usePutTenantIDPOnlyDomainHook>>>,
    TError,
    { tenantId: TenantIDDTO; domain: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof usePutTenantIDPOnlyDomainHook>>>,
  TError,
  { tenantId: TenantIDDTO; domain: string },
  TContext
> => {
  const mutationKey = ['putTenantIDPOnlyDomain'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const putTenantIDPOnlyDomain = usePutTenantIDPOnlyDomainHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof usePutTenantIDPOnlyDomainHook>>>,
    { tenantId: TenantIDDTO; domain: string }
  > = (props) => {
    const { tenantId, domain } = props ?? {};

    return putTenantIDPOnlyDomain(tenantId, domain);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutTenantIDPOnlyDomainMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof usePutTenantIDPOnlyDomainHook>>>
>;

export type PutTenantIDPOnlyDomainMutationError =
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
  | ConflictResponse;

/**
 * @summary Add an IdP-only domain to tenant
 */
export const usePutTenantIDPOnlyDomain = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | ConflictResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof usePutTenantIDPOnlyDomainHook>>>,
      TError,
      { tenantId: TenantIDDTO; domain: string },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof usePutTenantIDPOnlyDomainHook>>>,
  TError,
  { tenantId: TenantIDDTO; domain: string },
  TContext
> => {
  const mutationOptions = usePutTenantIDPOnlyDomainMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * @summary Remove an IdP-only domain from tenant
 */
export const useDeleteTenantIDPOnlyDomainHook = () => {
  const deleteTenantIDPOnlyDomain = useAxiosAuth<TenantIDPOnlyDomainDTO>();

  return useCallback(
    (tenantId: TenantIDDTO, domain: string) => {
      return deleteTenantIDPOnlyDomain({
        url: `/admin/tenants/${tenantId}/idp-only-domains/${domain}`,
        method: 'DELETE',
      });
    },
    [deleteTenantIDPOnlyDomain],
  );
};

export const useDeleteTenantIDPOnlyDomainMutationOptions = <
  TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteTenantIDPOnlyDomainHook>>>,
    TError,
    { tenantId: TenantIDDTO; domain: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useDeleteTenantIDPOnlyDomainHook>>>,
  TError,
  { tenantId: TenantIDDTO; domain: string },
  TContext
> => {
  const mutationKey = ['deleteTenantIDPOnlyDomain'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const deleteTenantIDPOnlyDomain = useDeleteTenantIDPOnlyDomainHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useDeleteTenantIDPOnlyDomainHook>>>,
    { tenantId: TenantIDDTO; domain: string }
  > = (props) => {
    const { tenantId, domain } = props ?? {};

    return deleteTenantIDPOnlyDomain(tenantId, domain);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteTenantIDPOnlyDomainMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useDeleteTenantIDPOnlyDomainHook>>>
>;

export type DeleteTenantIDPOnlyDomainMutationError =
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse;

/**
 * @summary Remove an IdP-only domain from tenant
 */
export const useDeleteTenantIDPOnlyDomain = <
  TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useDeleteTenantIDPOnlyDomainHook>>>,
      TError,
      { tenantId: TenantIDDTO; domain: string },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useDeleteTenantIDPOnlyDomainHook>>>,
  TError,
  { tenantId: TenantIDDTO; domain: string },
  TContext
> => {
  const mutationOptions = useDeleteTenantIDPOnlyDomainMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * @summary Upload or replace a tenant's icon
 */
export const usePutTenantIconHook = () => {
  const putTenantIcon = useAxiosAuth<IconUploadResponseDTO>();

  return useCallback(
    (tenantId: TenantIDDTO, iconUploadBodies: IconUploadBodies) => {
      return putTenantIcon({
        url: `/admin/tenants/${tenantId}/icon`,
        method: 'PUT',
        headers: { 'Content-Type': 'image/*' },
        data: iconUploadBodies,
      });
    },
    [putTenantIcon],
  );
};

export const usePutTenantIconMutationOptions = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | PayloadTooLargeResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof usePutTenantIconHook>>>,
    TError,
    { tenantId: TenantIDDTO; data: IconUploadBodies },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof usePutTenantIconHook>>>,
  TError,
  { tenantId: TenantIDDTO; data: IconUploadBodies },
  TContext
> => {
  const mutationKey = ['putTenantIcon'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const putTenantIcon = usePutTenantIconHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof usePutTenantIconHook>>>,
    { tenantId: TenantIDDTO; data: IconUploadBodies }
  > = (props) => {
    const { tenantId, data } = props ?? {};

    return putTenantIcon(tenantId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutTenantIconMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof usePutTenantIconHook>>>
>;
export type PutTenantIconMutationBody = IconUploadBodies;
export type PutTenantIconMutationError =
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
  | PayloadTooLargeResponse;

/**
 * @summary Upload or replace a tenant's icon
 */
export const usePutTenantIcon = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | PayloadTooLargeResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof usePutTenantIconHook>>>,
      TError,
      { tenantId: TenantIDDTO; data: IconUploadBodies },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof usePutTenantIconHook>>>,
  TError,
  { tenantId: TenantIDDTO; data: IconUploadBodies },
  TContext
> => {
  const mutationOptions = usePutTenantIconMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Succeeds also when there is no icon.
 * @summary Remove a tenant's icon
 */
export const useDeleteTenantIconHook = () => {
  const deleteTenantIcon = useAxiosAuth<void>();

  return useCallback(
    (tenantId: TenantIDDTO) => {
      return deleteTenantIcon({
        url: `/admin/tenants/${tenantId}/icon`,
        method: 'DELETE',
      });
    },
    [deleteTenantIcon],
  );
};

export const useDeleteTenantIconMutationOptions = <
  TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteTenantIconHook>>>,
    TError,
    { tenantId: TenantIDDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useDeleteTenantIconHook>>>,
  TError,
  { tenantId: TenantIDDTO },
  TContext
> => {
  const mutationKey = ['deleteTenantIcon'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const deleteTenantIcon = useDeleteTenantIconHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useDeleteTenantIconHook>>>,
    { tenantId: TenantIDDTO }
  > = (props) => {
    const { tenantId } = props ?? {};

    return deleteTenantIcon(tenantId);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteTenantIconMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useDeleteTenantIconHook>>>
>;

export type DeleteTenantIconMutationError =
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse;

/**
 * @summary Remove a tenant's icon
 */
export const useDeleteTenantIcon = <
  TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useDeleteTenantIconHook>>>,
      TError,
      { tenantId: TenantIDDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useDeleteTenantIconHook>>>,
  TError,
  { tenantId: TenantIDDTO },
  TContext
> => {
  const mutationOptions = useDeleteTenantIconMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * @summary Create a new application
 */
export const useCreateApplicationHook = () => {
  const createApplication = useAxiosAuth<ApplicationDTO>();

  return useCallback(
    (
      createApplicationRequestDTO: CreateApplicationRequestDTO,
      signal?: AbortSignal,
    ) => {
      return createApplication({
        url: `/admin/applications`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: createApplicationRequestDTO,
        signal,
      });
    },
    [createApplication],
  );
};

export const useCreateApplicationMutationOptions = <
  TError = BadRequestResponse | UnauthorizedResponse | ForbiddenResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useCreateApplicationHook>>>,
    TError,
    { data: CreateApplicationRequestDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useCreateApplicationHook>>>,
  TError,
  { data: CreateApplicationRequestDTO },
  TContext
> => {
  const mutationKey = ['createApplication'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const createApplication = useCreateApplicationHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useCreateApplicationHook>>>,
    { data: CreateApplicationRequestDTO }
  > = (props) => {
    const { data } = props ?? {};

    return createApplication(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateApplicationMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useCreateApplicationHook>>>
>;
export type CreateApplicationMutationBody = CreateApplicationRequestDTO;
export type CreateApplicationMutationError =
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse;

/**
 * @summary Create a new application
 */
export const useCreateApplication = <
  TError = BadRequestResponse | UnauthorizedResponse | ForbiddenResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useCreateApplicationHook>>>,
      TError,
      { data: CreateApplicationRequestDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useCreateApplicationHook>>>,
  TError,
  { data: CreateApplicationRequestDTO },
  TContext
> => {
  const mutationOptions = useCreateApplicationMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * @summary List all applications
 */
export const useListApplicationsHook = () => {
  const listApplications = useAxiosAuth<ApplicationDTO[]>();

  return useCallback(
    (signal?: AbortSignal) => {
      return listApplications({
        url: `/admin/applications`,
        method: 'GET',
        signal,
      });
    },
    [listApplications],
  );
};

export const getListApplicationsQueryKey = () => {
  return [`/admin/applications`] as const;
};

export const useListApplicationsQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useListApplicationsHook>>>,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<ReturnType<typeof useListApplicationsHook>>>,
      TError,
      TData
    >
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListApplicationsQueryKey();

  const listApplications = useListApplicationsHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useListApplicationsHook>>>
  > = ({ signal }) => listApplications(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useListApplicationsHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type ListApplicationsQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useListApplicationsHook>>>
>;
export type ListApplicationsQueryError =
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse;

export function useListApplications<
  TData = Awaited<ReturnType<ReturnType<typeof useListApplicationsHook>>>,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListApplicationsHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useListApplicationsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useListApplicationsHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useListApplications<
  TData = Awaited<ReturnType<ReturnType<typeof useListApplicationsHook>>>,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListApplicationsHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useListApplicationsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useListApplicationsHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useListApplications<
  TData = Awaited<ReturnType<ReturnType<typeof useListApplicationsHook>>>,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListApplicationsHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary List all applications
 */

export function useListApplications<
  TData = Awaited<ReturnType<ReturnType<typeof useListApplicationsHook>>>,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListApplicationsHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useListApplicationsQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Get a specific application
 */
export const useGetApplicationHook = () => {
  const getApplication = useAxiosAuth<ApplicationDTO>();

  return useCallback(
    (applicationId: string, signal?: AbortSignal) => {
      return getApplication({
        url: `/admin/applications/${applicationId}`,
        method: 'GET',
        signal,
      });
    },
    [getApplication],
  );
};

export const getGetApplicationQueryKey = (applicationId?: string) => {
  return [`/admin/applications/${applicationId}`] as const;
};

export const useGetApplicationQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetApplicationHook>>>,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(
  applicationId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetApplicationHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetApplicationQueryKey(applicationId);

  const getApplication = useGetApplicationHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGetApplicationHook>>>
  > = ({ signal }) => getApplication(applicationId, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!applicationId,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetApplicationHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetApplicationQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetApplicationHook>>>
>;
export type GetApplicationQueryError =
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse;

export function useGetApplication<
  TData = Awaited<ReturnType<ReturnType<typeof useGetApplicationHook>>>,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(
  applicationId: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetApplicationHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetApplicationHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetApplicationHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetApplication<
  TData = Awaited<ReturnType<ReturnType<typeof useGetApplicationHook>>>,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(
  applicationId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetApplicationHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetApplicationHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetApplicationHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetApplication<
  TData = Awaited<ReturnType<ReturnType<typeof useGetApplicationHook>>>,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(
  applicationId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetApplicationHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get a specific application
 */

export function useGetApplication<
  TData = Awaited<ReturnType<ReturnType<typeof useGetApplicationHook>>>,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(
  applicationId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetApplicationHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useGetApplicationQueryOptions(applicationId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Replaces the application's name, uris and role_ids with the provided data.
description, enable_manage_operations, organization_management and
cross_application_organization_profiles are only changed when present
in the request body; omitted fields keep their stored value.
Setting organization_management to `disabled` while organizations are
linked to the application returns 400. Removing a role that user
profiles still use returns 409; remove those profiles first.

 * @summary Update an application
 */
export const useUpdateApplicationHook = () => {
  const updateApplication = useAxiosAuth<ApplicationDTO>();

  return useCallback(
    (
      applicationId: string,
      updateApplicationRequestDTO: UpdateApplicationRequestDTO,
    ) => {
      return updateApplication({
        url: `/admin/applications/${applicationId}`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        data: updateApplicationRequestDTO,
      });
    },
    [updateApplication],
  );
};

export const useUpdateApplicationMutationOptions = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | ConflictResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdateApplicationHook>>>,
    TError,
    { applicationId: string; data: UpdateApplicationRequestDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useUpdateApplicationHook>>>,
  TError,
  { applicationId: string; data: UpdateApplicationRequestDTO },
  TContext
> => {
  const mutationKey = ['updateApplication'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const updateApplication = useUpdateApplicationHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useUpdateApplicationHook>>>,
    { applicationId: string; data: UpdateApplicationRequestDTO }
  > = (props) => {
    const { applicationId, data } = props ?? {};

    return updateApplication(applicationId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateApplicationMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useUpdateApplicationHook>>>
>;
export type UpdateApplicationMutationBody = UpdateApplicationRequestDTO;
export type UpdateApplicationMutationError =
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
  | ConflictResponse;

/**
 * @summary Update an application
 */
export const useUpdateApplication = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | ConflictResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useUpdateApplicationHook>>>,
      TError,
      { applicationId: string; data: UpdateApplicationRequestDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useUpdateApplicationHook>>>,
  TError,
  { applicationId: string; data: UpdateApplicationRequestDTO },
  TContext
> => {
  const mutationOptions = useUpdateApplicationMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * @summary Delete an application
 */
export const useDeleteApplicationHook = () => {
  const deleteApplication = useAxiosAuth<ApplicationDTO>();

  return useCallback(
    (applicationId: string) => {
      return deleteApplication({
        url: `/admin/applications/${applicationId}`,
        method: 'DELETE',
      });
    },
    [deleteApplication],
  );
};

export const useDeleteApplicationMutationOptions = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteApplicationHook>>>,
    TError,
    { applicationId: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useDeleteApplicationHook>>>,
  TError,
  { applicationId: string },
  TContext
> => {
  const mutationKey = ['deleteApplication'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const deleteApplication = useDeleteApplicationHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useDeleteApplicationHook>>>,
    { applicationId: string }
  > = (props) => {
    const { applicationId } = props ?? {};

    return deleteApplication(applicationId);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteApplicationMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useDeleteApplicationHook>>>
>;

export type DeleteApplicationMutationError =
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse;

/**
 * @summary Delete an application
 */
export const useDeleteApplication = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useDeleteApplicationHook>>>,
      TError,
      { applicationId: string },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useDeleteApplicationHook>>>,
  TError,
  { applicationId: string },
  TContext
> => {
  const mutationOptions = useDeleteApplicationMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Replace the existing roles for an application.
The provided roles array represents the complete set
of roles that will be applied to the application
after this operation. Removing a role that user profiles
still use returns 409; remove those profiles first.

 * @summary Update application roles
 */
export const useUpdateApplicationRolesHook = () => {
  const updateApplicationRoles = useAxiosAuth<ApplicationRoleIDDTO[]>();

  return useCallback(
    (
      applicationId: string,
      updateApplicationRolesRequestDTO: UpdateApplicationRolesRequestDTO,
    ) => {
      return updateApplicationRoles({
        url: `/admin/applications/${applicationId}/roles`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        data: updateApplicationRolesRequestDTO,
      });
    },
    [updateApplicationRoles],
  );
};

export const useUpdateApplicationRolesMutationOptions = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | ConflictResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdateApplicationRolesHook>>>,
    TError,
    { applicationId: string; data: UpdateApplicationRolesRequestDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useUpdateApplicationRolesHook>>>,
  TError,
  { applicationId: string; data: UpdateApplicationRolesRequestDTO },
  TContext
> => {
  const mutationKey = ['updateApplicationRoles'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const updateApplicationRoles = useUpdateApplicationRolesHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useUpdateApplicationRolesHook>>>,
    { applicationId: string; data: UpdateApplicationRolesRequestDTO }
  > = (props) => {
    const { applicationId, data } = props ?? {};

    return updateApplicationRoles(applicationId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateApplicationRolesMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useUpdateApplicationRolesHook>>>
>;
export type UpdateApplicationRolesMutationBody =
  UpdateApplicationRolesRequestDTO;
export type UpdateApplicationRolesMutationError =
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
  | ConflictResponse;

/**
 * @summary Update application roles
 */
export const useUpdateApplicationRoles = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | ConflictResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useUpdateApplicationRolesHook>>>,
      TError,
      { applicationId: string; data: UpdateApplicationRolesRequestDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useUpdateApplicationRolesHook>>>,
  TError,
  { applicationId: string; data: UpdateApplicationRolesRequestDTO },
  TContext
> => {
  const mutationOptions = useUpdateApplicationRolesMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Replace the existing uris for an application.
The provided uris array represents the complete set
of uris that will be applied to the application
after this operation.

 * @summary Update applications URIs
 */
export const useUpdateApplicationURIsHook = () => {
  const updateApplicationURIs = useAxiosAuth<ApplicationURIDTO[]>();

  return useCallback(
    (
      applicationId: string,
      updateApplicationURIsRequestDTO: UpdateApplicationURIsRequestDTO,
    ) => {
      return updateApplicationURIs({
        url: `/admin/applications/${applicationId}/uris`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        data: updateApplicationURIsRequestDTO,
      });
    },
    [updateApplicationURIs],
  );
};

export const useUpdateApplicationURIsMutationOptions = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdateApplicationURIsHook>>>,
    TError,
    { applicationId: string; data: UpdateApplicationURIsRequestDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useUpdateApplicationURIsHook>>>,
  TError,
  { applicationId: string; data: UpdateApplicationURIsRequestDTO },
  TContext
> => {
  const mutationKey = ['updateApplicationURIs'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const updateApplicationURIs = useUpdateApplicationURIsHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useUpdateApplicationURIsHook>>>,
    { applicationId: string; data: UpdateApplicationURIsRequestDTO }
  > = (props) => {
    const { applicationId, data } = props ?? {};

    return updateApplicationURIs(applicationId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateApplicationURIsMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useUpdateApplicationURIsHook>>>
>;
export type UpdateApplicationURIsMutationBody = UpdateApplicationURIsRequestDTO;
export type UpdateApplicationURIsMutationError =
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse;

/**
 * @summary Update applications URIs
 */
export const useUpdateApplicationURIs = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useUpdateApplicationURIsHook>>>,
      TError,
      { applicationId: string; data: UpdateApplicationURIsRequestDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useUpdateApplicationURIsHook>>>,
  TError,
  { applicationId: string; data: UpdateApplicationURIsRequestDTO },
  TContext
> => {
  const mutationOptions = useUpdateApplicationURIsMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * @summary Upload or replace an application's icon
 */
export const usePutApplicationIconHook = () => {
  const putApplicationIcon = useAxiosAuth<IconUploadResponseDTO>();

  return useCallback(
    (applicationId: string, iconUploadBodies: IconUploadBodies) => {
      return putApplicationIcon({
        url: `/admin/applications/${applicationId}/icon`,
        method: 'PUT',
        headers: { 'Content-Type': 'image/*' },
        data: iconUploadBodies,
      });
    },
    [putApplicationIcon],
  );
};

export const usePutApplicationIconMutationOptions = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | PayloadTooLargeResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof usePutApplicationIconHook>>>,
    TError,
    { applicationId: string; data: IconUploadBodies },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof usePutApplicationIconHook>>>,
  TError,
  { applicationId: string; data: IconUploadBodies },
  TContext
> => {
  const mutationKey = ['putApplicationIcon'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const putApplicationIcon = usePutApplicationIconHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof usePutApplicationIconHook>>>,
    { applicationId: string; data: IconUploadBodies }
  > = (props) => {
    const { applicationId, data } = props ?? {};

    return putApplicationIcon(applicationId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutApplicationIconMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof usePutApplicationIconHook>>>
>;
export type PutApplicationIconMutationBody = IconUploadBodies;
export type PutApplicationIconMutationError =
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
  | PayloadTooLargeResponse;

/**
 * @summary Upload or replace an application's icon
 */
export const usePutApplicationIcon = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | PayloadTooLargeResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof usePutApplicationIconHook>>>,
      TError,
      { applicationId: string; data: IconUploadBodies },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof usePutApplicationIconHook>>>,
  TError,
  { applicationId: string; data: IconUploadBodies },
  TContext
> => {
  const mutationOptions = usePutApplicationIconMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Succeeds also when there is no icon.
 * @summary Remove an application's icon
 */
export const useDeleteApplicationIconHook = () => {
  const deleteApplicationIcon = useAxiosAuth<void>();

  return useCallback(
    (applicationId: string) => {
      return deleteApplicationIcon({
        url: `/admin/applications/${applicationId}/icon`,
        method: 'DELETE',
      });
    },
    [deleteApplicationIcon],
  );
};

export const useDeleteApplicationIconMutationOptions = <
  TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteApplicationIconHook>>>,
    TError,
    { applicationId: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useDeleteApplicationIconHook>>>,
  TError,
  { applicationId: string },
  TContext
> => {
  const mutationKey = ['deleteApplicationIcon'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const deleteApplicationIcon = useDeleteApplicationIconHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useDeleteApplicationIconHook>>>,
    { applicationId: string }
  > = (props) => {
    const { applicationId } = props ?? {};

    return deleteApplicationIcon(applicationId);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteApplicationIconMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useDeleteApplicationIconHook>>>
>;

export type DeleteApplicationIconMutationError =
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse;

/**
 * @summary Remove an application's icon
 */
export const useDeleteApplicationIcon = <
  TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useDeleteApplicationIconHook>>>,
      TError,
      { applicationId: string },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useDeleteApplicationIconHook>>>,
  TError,
  { applicationId: string },
  TContext
> => {
  const mutationOptions = useDeleteApplicationIconMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * @summary Create a tenant application
 */
export const useCreateTenantApplicationHook = () => {
  const createTenantApplication = useAxiosAuth<TenantApplicationDTO>();

  return useCallback(
    (
      createTenantApplicationRequestDTO: CreateTenantApplicationRequestDTO,
      signal?: AbortSignal,
    ) => {
      return createTenantApplication({
        url: `/admin/tenant-applications`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: createTenantApplicationRequestDTO,
        signal,
      });
    },
    [createTenantApplication],
  );
};

export const useCreateTenantApplicationMutationOptions = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useCreateTenantApplicationHook>>>,
    TError,
    { data: CreateTenantApplicationRequestDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useCreateTenantApplicationHook>>>,
  TError,
  { data: CreateTenantApplicationRequestDTO },
  TContext
> => {
  const mutationKey = ['createTenantApplication'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const createTenantApplication = useCreateTenantApplicationHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useCreateTenantApplicationHook>>>,
    { data: CreateTenantApplicationRequestDTO }
  > = (props) => {
    const { data } = props ?? {};

    return createTenantApplication(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateTenantApplicationMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useCreateTenantApplicationHook>>>
>;
export type CreateTenantApplicationMutationBody =
  CreateTenantApplicationRequestDTO;
export type CreateTenantApplicationMutationError =
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse;

/**
 * @summary Create a tenant application
 */
export const useCreateTenantApplication = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useCreateTenantApplicationHook>>>,
      TError,
      { data: CreateTenantApplicationRequestDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useCreateTenantApplicationHook>>>,
  TError,
  { data: CreateTenantApplicationRequestDTO },
  TContext
> => {
  const mutationOptions = useCreateTenantApplicationMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * @summary List all tenant applications
 */
export const useListTenantApplicationsHook = () => {
  const listTenantApplications = useAxiosAuth<TenantApplicationDTO[]>();

  return useCallback(
    (signal?: AbortSignal) => {
      return listTenantApplications({
        url: `/admin/tenant-applications`,
        method: 'GET',
        signal,
      });
    },
    [listTenantApplications],
  );
};

export const getListTenantApplicationsQueryKey = () => {
  return [`/admin/tenant-applications`] as const;
};

export const useListTenantApplicationsQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useListTenantApplicationsHook>>>,
  TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<ReturnType<typeof useListTenantApplicationsHook>>>,
      TError,
      TData
    >
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListTenantApplicationsQueryKey();

  const listTenantApplications = useListTenantApplicationsHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useListTenantApplicationsHook>>>
  > = ({ signal }) => listTenantApplications(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useListTenantApplicationsHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type ListTenantApplicationsQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useListTenantApplicationsHook>>>
>;
export type ListTenantApplicationsQueryError =
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse;

export function useListTenantApplications<
  TData = Awaited<ReturnType<ReturnType<typeof useListTenantApplicationsHook>>>,
  TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListTenantApplicationsHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useListTenantApplicationsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useListTenantApplicationsHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useListTenantApplications<
  TData = Awaited<ReturnType<ReturnType<typeof useListTenantApplicationsHook>>>,
  TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListTenantApplicationsHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useListTenantApplicationsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useListTenantApplicationsHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useListTenantApplications<
  TData = Awaited<ReturnType<ReturnType<typeof useListTenantApplicationsHook>>>,
  TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListTenantApplicationsHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary List all tenant applications
 */

export function useListTenantApplications<
  TData = Awaited<ReturnType<ReturnType<typeof useListTenantApplicationsHook>>>,
  TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListTenantApplicationsHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useListTenantApplicationsQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Get a specific tenant application
 */
export const useGetTenantApplicationHook = () => {
  const getTenantApplication = useAxiosAuth<TenantApplicationDTO>();

  return useCallback(
    (tenantApplicationId: string, signal?: AbortSignal) => {
      return getTenantApplication({
        url: `/admin/tenant-applications/${tenantApplicationId}`,
        method: 'GET',
        signal,
      });
    },
    [getTenantApplication],
  );
};

export const getGetTenantApplicationQueryKey = (
  tenantApplicationId?: string,
) => {
  return [`/admin/tenant-applications/${tenantApplicationId}`] as const;
};

export const useGetTenantApplicationQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetTenantApplicationHook>>>,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(
  tenantApplicationId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTenantApplicationHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetTenantApplicationQueryKey(tenantApplicationId);

  const getTenantApplication = useGetTenantApplicationHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGetTenantApplicationHook>>>
  > = ({ signal }) => getTenantApplication(tenantApplicationId, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!tenantApplicationId,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetTenantApplicationHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetTenantApplicationQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetTenantApplicationHook>>>
>;
export type GetTenantApplicationQueryError =
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse;

export function useGetTenantApplication<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTenantApplicationHook>>>,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(
  tenantApplicationId: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTenantApplicationHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetTenantApplicationHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetTenantApplicationHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetTenantApplication<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTenantApplicationHook>>>,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(
  tenantApplicationId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTenantApplicationHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetTenantApplicationHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetTenantApplicationHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetTenantApplication<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTenantApplicationHook>>>,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(
  tenantApplicationId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTenantApplicationHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get a specific tenant application
 */

export function useGetTenantApplication<
  TData = Awaited<ReturnType<ReturnType<typeof useGetTenantApplicationHook>>>,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(
  tenantApplicationId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTenantApplicationHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useGetTenantApplicationQueryOptions(
    tenantApplicationId,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Update tenant application
 */
export const useUpdateTenantApplicationHook = () => {
  const updateTenantApplication = useAxiosAuth<TenantApplicationDTO>();

  return useCallback(
    (
      tenantApplicationId: string,
      updateTenantApplicationRequestDTO: UpdateTenantApplicationRequestDTO,
      signal?: AbortSignal,
    ) => {
      return updateTenantApplication({
        url: `/admin/tenant-applications/${tenantApplicationId}`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: updateTenantApplicationRequestDTO,
        signal,
      });
    },
    [updateTenantApplication],
  );
};

export const useUpdateTenantApplicationMutationOptions = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdateTenantApplicationHook>>>,
    TError,
    { tenantApplicationId: string; data: UpdateTenantApplicationRequestDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useUpdateTenantApplicationHook>>>,
  TError,
  { tenantApplicationId: string; data: UpdateTenantApplicationRequestDTO },
  TContext
> => {
  const mutationKey = ['updateTenantApplication'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const updateTenantApplication = useUpdateTenantApplicationHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useUpdateTenantApplicationHook>>>,
    { tenantApplicationId: string; data: UpdateTenantApplicationRequestDTO }
  > = (props) => {
    const { tenantApplicationId, data } = props ?? {};

    return updateTenantApplication(tenantApplicationId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateTenantApplicationMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useUpdateTenantApplicationHook>>>
>;
export type UpdateTenantApplicationMutationBody =
  UpdateTenantApplicationRequestDTO;
export type UpdateTenantApplicationMutationError =
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse;

/**
 * @summary Update tenant application
 */
export const useUpdateTenantApplication = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useUpdateTenantApplicationHook>>>,
      TError,
      { tenantApplicationId: string; data: UpdateTenantApplicationRequestDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useUpdateTenantApplicationHook>>>,
  TError,
  { tenantApplicationId: string; data: UpdateTenantApplicationRequestDTO },
  TContext
> => {
  const mutationOptions = useUpdateTenantApplicationMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * @summary Delete a tenant application
 */
export const useDeleteTenantApplicationHook = () => {
  const deleteTenantApplication = useAxiosAuth<TenantApplicationDTO>();

  return useCallback(
    (tenantApplicationId: string) => {
      return deleteTenantApplication({
        url: `/admin/tenant-applications/${tenantApplicationId}`,
        method: 'DELETE',
      });
    },
    [deleteTenantApplication],
  );
};

export const useDeleteTenantApplicationMutationOptions = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteTenantApplicationHook>>>,
    TError,
    { tenantApplicationId: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useDeleteTenantApplicationHook>>>,
  TError,
  { tenantApplicationId: string },
  TContext
> => {
  const mutationKey = ['deleteTenantApplication'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const deleteTenantApplication = useDeleteTenantApplicationHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useDeleteTenantApplicationHook>>>,
    { tenantApplicationId: string }
  > = (props) => {
    const { tenantApplicationId } = props ?? {};

    return deleteTenantApplication(tenantApplicationId);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteTenantApplicationMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useDeleteTenantApplicationHook>>>
>;

export type DeleteTenantApplicationMutationError =
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse;

/**
 * @summary Delete a tenant application
 */
export const useDeleteTenantApplication = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useDeleteTenantApplicationHook>>>,
      TError,
      { tenantApplicationId: string },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useDeleteTenantApplicationHook>>>,
  TError,
  { tenantApplicationId: string },
  TContext
> => {
  const mutationOptions = useDeleteTenantApplicationMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * @summary Create a new role
 */
export const useCreateRoleHook = () => {
  const createRole = useAxiosAuth<RoleDTO>();

  return useCallback(
    (createRoleRequestDTO: CreateRoleRequestDTO, signal?: AbortSignal) => {
      return createRole({
        url: `/admin/roles`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: createRoleRequestDTO,
        signal,
      });
    },
    [createRole],
  );
};

export const useCreateRoleMutationOptions = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useCreateRoleHook>>>,
    TError,
    { data: CreateRoleRequestDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useCreateRoleHook>>>,
  TError,
  { data: CreateRoleRequestDTO },
  TContext
> => {
  const mutationKey = ['createRole'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const createRole = useCreateRoleHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useCreateRoleHook>>>,
    { data: CreateRoleRequestDTO }
  > = (props) => {
    const { data } = props ?? {};

    return createRole(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateRoleMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useCreateRoleHook>>>
>;
export type CreateRoleMutationBody = CreateRoleRequestDTO;
export type CreateRoleMutationError =
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse;

/**
 * @summary Create a new role
 */
export const useCreateRole = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useCreateRoleHook>>>,
      TError,
      { data: CreateRoleRequestDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useCreateRoleHook>>>,
  TError,
  { data: CreateRoleRequestDTO },
  TContext
> => {
  const mutationOptions = useCreateRoleMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * @summary List all roles
 */
export const useListRolesHook = () => {
  const listRoles = useAxiosAuth<RoleDTO[]>();

  return useCallback(
    (signal?: AbortSignal) => {
      return listRoles({ url: `/admin/roles`, method: 'GET', signal });
    },
    [listRoles],
  );
};

export const getListRolesQueryKey = () => {
  return [`/admin/roles`] as const;
};

export const useListRolesQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useListRolesHook>>>,
  TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<ReturnType<typeof useListRolesHook>>>,
      TError,
      TData
    >
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListRolesQueryKey();

  const listRoles = useListRolesHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useListRolesHook>>>
  > = ({ signal }) => listRoles(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useListRolesHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type ListRolesQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useListRolesHook>>>
>;
export type ListRolesQueryError =
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse;

export function useListRoles<
  TData = Awaited<ReturnType<ReturnType<typeof useListRolesHook>>>,
  TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListRolesHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useListRolesHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useListRolesHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useListRoles<
  TData = Awaited<ReturnType<ReturnType<typeof useListRolesHook>>>,
  TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListRolesHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useListRolesHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useListRolesHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useListRoles<
  TData = Awaited<ReturnType<ReturnType<typeof useListRolesHook>>>,
  TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListRolesHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary List all roles
 */

export function useListRoles<
  TData = Awaited<ReturnType<ReturnType<typeof useListRolesHook>>>,
  TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListRolesHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useListRolesQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Get a specific role
 */
export const useGetRoleHook = () => {
  const getRole = useAxiosAuth<RoleDTO>();

  return useCallback(
    (roleId: string, signal?: AbortSignal) => {
      return getRole({ url: `/admin/roles/${roleId}`, method: 'GET', signal });
    },
    [getRole],
  );
};

export const getGetRoleQueryKey = (roleId?: string) => {
  return [`/admin/roles/${roleId}`] as const;
};

export const useGetRoleQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetRoleHook>>>,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(
  roleId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetRoleHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetRoleQueryKey(roleId);

  const getRole = useGetRoleHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGetRoleHook>>>
  > = ({ signal }) => getRole(roleId, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!roleId,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetRoleHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetRoleQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetRoleHook>>>
>;
export type GetRoleQueryError =
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse;

export function useGetRole<
  TData = Awaited<ReturnType<ReturnType<typeof useGetRoleHook>>>,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(
  roleId: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetRoleHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetRoleHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetRoleHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetRole<
  TData = Awaited<ReturnType<ReturnType<typeof useGetRoleHook>>>,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(
  roleId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetRoleHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetRoleHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetRoleHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetRole<
  TData = Awaited<ReturnType<ReturnType<typeof useGetRoleHook>>>,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(
  roleId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetRoleHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get a specific role
 */

export function useGetRole<
  TData = Awaited<ReturnType<ReturnType<typeof useGetRoleHook>>>,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(
  roleId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetRoleHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useGetRoleQueryOptions(roleId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Replaces the role's name, description, parent and permissions with the provided data.
`organization_mode` is left unchanged when omitted.

 * @summary Update a role
 */
export const useUpdateRoleHook = () => {
  const updateRole = useAxiosAuth<RoleDTO>();

  return useCallback(
    (roleId: string, updateRoleRequestDTO: UpdateRoleRequestDTO) => {
      return updateRole({
        url: `/admin/roles/${roleId}`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        data: updateRoleRequestDTO,
      });
    },
    [updateRole],
  );
};

export const useUpdateRoleMutationOptions = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdateRoleHook>>>,
    TError,
    { roleId: string; data: UpdateRoleRequestDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useUpdateRoleHook>>>,
  TError,
  { roleId: string; data: UpdateRoleRequestDTO },
  TContext
> => {
  const mutationKey = ['updateRole'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const updateRole = useUpdateRoleHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useUpdateRoleHook>>>,
    { roleId: string; data: UpdateRoleRequestDTO }
  > = (props) => {
    const { roleId, data } = props ?? {};

    return updateRole(roleId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateRoleMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useUpdateRoleHook>>>
>;
export type UpdateRoleMutationBody = UpdateRoleRequestDTO;
export type UpdateRoleMutationError =
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse;

/**
 * @summary Update a role
 */
export const useUpdateRole = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useUpdateRoleHook>>>,
      TError,
      { roleId: string; data: UpdateRoleRequestDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useUpdateRoleHook>>>,
  TError,
  { roleId: string; data: UpdateRoleRequestDTO },
  TContext
> => {
  const mutationOptions = useUpdateRoleMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Deleting a role that user profiles still use returns 409; remove those
profiles first.

 * @summary Delete a role
 */
export const useDeleteRoleHook = () => {
  const deleteRole = useAxiosAuth<RoleDTO>();

  return useCallback(
    (roleId: string) => {
      return deleteRole({ url: `/admin/roles/${roleId}`, method: 'DELETE' });
    },
    [deleteRole],
  );
};

export const useDeleteRoleMutationOptions = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | ConflictResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteRoleHook>>>,
    TError,
    { roleId: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useDeleteRoleHook>>>,
  TError,
  { roleId: string },
  TContext
> => {
  const mutationKey = ['deleteRole'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const deleteRole = useDeleteRoleHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useDeleteRoleHook>>>,
    { roleId: string }
  > = (props) => {
    const { roleId } = props ?? {};

    return deleteRole(roleId);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteRoleMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useDeleteRoleHook>>>
>;

export type DeleteRoleMutationError =
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
  | ConflictResponse;

/**
 * @summary Delete a role
 */
export const useDeleteRole = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | ConflictResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useDeleteRoleHook>>>,
      TError,
      { roleId: string },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useDeleteRoleHook>>>,
  TError,
  { roleId: string },
  TContext
> => {
  const mutationOptions = useDeleteRoleMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Replace the existing permissions for a role.
The provided permissions array represents the complete set
of permissions that will be applied to the API after this operation.

 * @summary Update role permissions
 */
export const useUpdateRolePermissionsHook = () => {
  const updateRolePermissions = useAxiosAuth<RolePermissionDTO[]>();

  return useCallback(
    (
      roleId: string,
      updateRolePermissionsRequestDTO: UpdateRolePermissionsRequestDTO,
    ) => {
      return updateRolePermissions({
        url: `/admin/roles/${roleId}/permissions`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        data: updateRolePermissionsRequestDTO,
      });
    },
    [updateRolePermissions],
  );
};

export const useUpdateRolePermissionsMutationOptions = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdateRolePermissionsHook>>>,
    TError,
    { roleId: string; data: UpdateRolePermissionsRequestDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useUpdateRolePermissionsHook>>>,
  TError,
  { roleId: string; data: UpdateRolePermissionsRequestDTO },
  TContext
> => {
  const mutationKey = ['updateRolePermissions'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const updateRolePermissions = useUpdateRolePermissionsHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useUpdateRolePermissionsHook>>>,
    { roleId: string; data: UpdateRolePermissionsRequestDTO }
  > = (props) => {
    const { roleId, data } = props ?? {};

    return updateRolePermissions(roleId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateRolePermissionsMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useUpdateRolePermissionsHook>>>
>;
export type UpdateRolePermissionsMutationBody = UpdateRolePermissionsRequestDTO;
export type UpdateRolePermissionsMutationError =
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse;

/**
 * @summary Update role permissions
 */
export const useUpdateRolePermissions = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useUpdateRolePermissionsHook>>>,
      TError,
      { roleId: string; data: UpdateRolePermissionsRequestDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useUpdateRolePermissionsHook>>>,
  TError,
  { roleId: string; data: UpdateRolePermissionsRequestDTO },
  TContext
> => {
  const mutationOptions = useUpdateRolePermissionsMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * @summary Create a new API
 */
export const useCreateAPIHook = () => {
  const createAPI = useAxiosAuth<ApiDTO>();

  return useCallback(
    (createAPIRequestDTO: CreateAPIRequestDTO, signal?: AbortSignal) => {
      return createAPI({
        url: `/admin/apis`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: createAPIRequestDTO,
        signal,
      });
    },
    [createAPI],
  );
};

export const useCreateAPIMutationOptions = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useCreateAPIHook>>>,
    TError,
    { data: CreateAPIRequestDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useCreateAPIHook>>>,
  TError,
  { data: CreateAPIRequestDTO },
  TContext
> => {
  const mutationKey = ['createAPI'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const createAPI = useCreateAPIHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useCreateAPIHook>>>,
    { data: CreateAPIRequestDTO }
  > = (props) => {
    const { data } = props ?? {};

    return createAPI(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateAPIMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useCreateAPIHook>>>
>;
export type CreateAPIMutationBody = CreateAPIRequestDTO;
export type CreateAPIMutationError =
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse;

/**
 * @summary Create a new API
 */
export const useCreateAPI = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useCreateAPIHook>>>,
      TError,
      { data: CreateAPIRequestDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useCreateAPIHook>>>,
  TError,
  { data: CreateAPIRequestDTO },
  TContext
> => {
  const mutationOptions = useCreateAPIMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * @summary List all APIs
 */
export const useListAPIsHook = () => {
  const listAPIs = useAxiosAuth<ApiDTO[]>();

  return useCallback(
    (signal?: AbortSignal) => {
      return listAPIs({ url: `/admin/apis`, method: 'GET', signal });
    },
    [listAPIs],
  );
};

export const getListAPIsQueryKey = () => {
  return [`/admin/apis`] as const;
};

export const useListAPIsQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useListAPIsHook>>>,
  TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<ReturnType<typeof useListAPIsHook>>>,
      TError,
      TData
    >
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListAPIsQueryKey();

  const listAPIs = useListAPIsHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useListAPIsHook>>>
  > = ({ signal }) => listAPIs(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useListAPIsHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type ListAPIsQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useListAPIsHook>>>
>;
export type ListAPIsQueryError =
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse;

export function useListAPIs<
  TData = Awaited<ReturnType<ReturnType<typeof useListAPIsHook>>>,
  TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListAPIsHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useListAPIsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useListAPIsHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useListAPIs<
  TData = Awaited<ReturnType<ReturnType<typeof useListAPIsHook>>>,
  TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListAPIsHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useListAPIsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useListAPIsHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useListAPIs<
  TData = Awaited<ReturnType<ReturnType<typeof useListAPIsHook>>>,
  TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListAPIsHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary List all APIs
 */

export function useListAPIs<
  TData = Awaited<ReturnType<ReturnType<typeof useListAPIsHook>>>,
  TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListAPIsHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useListAPIsQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Get a specific API
 */
export const useGetAPIHook = () => {
  const getAPI = useAxiosAuth<ApiDTO>();

  return useCallback(
    (apiId: string, signal?: AbortSignal) => {
      return getAPI({ url: `/admin/apis/${apiId}`, method: 'GET', signal });
    },
    [getAPI],
  );
};

export const getGetAPIQueryKey = (apiId?: string) => {
  return [`/admin/apis/${apiId}`] as const;
};

export const useGetAPIQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetAPIHook>>>,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(
  apiId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetAPIHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetAPIQueryKey(apiId);

  const getAPI = useGetAPIHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGetAPIHook>>>
  > = ({ signal }) => getAPI(apiId, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!apiId,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetAPIHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetAPIQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetAPIHook>>>
>;
export type GetAPIQueryError =
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse;

export function useGetAPI<
  TData = Awaited<ReturnType<ReturnType<typeof useGetAPIHook>>>,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(
  apiId: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetAPIHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetAPIHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetAPIHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetAPI<
  TData = Awaited<ReturnType<ReturnType<typeof useGetAPIHook>>>,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(
  apiId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetAPIHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetAPIHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetAPIHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetAPI<
  TData = Awaited<ReturnType<ReturnType<typeof useGetAPIHook>>>,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(
  apiId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetAPIHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get a specific API
 */

export function useGetAPI<
  TData = Awaited<ReturnType<ReturnType<typeof useGetAPIHook>>>,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(
  apiId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetAPIHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useGetAPIQueryOptions(apiId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Replaces the entire existing API with the provided data.
This operation will overwrite all current API settings.

 * @summary Update an API
 */
export const useUpdateAPIHook = () => {
  const updateAPI = useAxiosAuth<ApiDTO>();

  return useCallback(
    (apiId: string, updateAPIRequestDTO: UpdateAPIRequestDTO) => {
      return updateAPI({
        url: `/admin/apis/${apiId}`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        data: updateAPIRequestDTO,
      });
    },
    [updateAPI],
  );
};

export const useUpdateAPIMutationOptions = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdateAPIHook>>>,
    TError,
    { apiId: string; data: UpdateAPIRequestDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useUpdateAPIHook>>>,
  TError,
  { apiId: string; data: UpdateAPIRequestDTO },
  TContext
> => {
  const mutationKey = ['updateAPI'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const updateAPI = useUpdateAPIHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useUpdateAPIHook>>>,
    { apiId: string; data: UpdateAPIRequestDTO }
  > = (props) => {
    const { apiId, data } = props ?? {};

    return updateAPI(apiId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateAPIMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useUpdateAPIHook>>>
>;
export type UpdateAPIMutationBody = UpdateAPIRequestDTO;
export type UpdateAPIMutationError =
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse;

/**
 * @summary Update an API
 */
export const useUpdateAPI = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useUpdateAPIHook>>>,
      TError,
      { apiId: string; data: UpdateAPIRequestDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useUpdateAPIHook>>>,
  TError,
  { apiId: string; data: UpdateAPIRequestDTO },
  TContext
> => {
  const mutationOptions = useUpdateAPIMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * @summary Delete API
 */
export const useDeleteAPIHook = () => {
  const deleteAPI = useAxiosAuth<ApiDTO>();

  return useCallback(
    (apiId: string) => {
      return deleteAPI({ url: `/admin/apis/${apiId}`, method: 'DELETE' });
    },
    [deleteAPI],
  );
};

export const useDeleteAPIMutationOptions = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteAPIHook>>>,
    TError,
    { apiId: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useDeleteAPIHook>>>,
  TError,
  { apiId: string },
  TContext
> => {
  const mutationKey = ['deleteAPI'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const deleteAPI = useDeleteAPIHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useDeleteAPIHook>>>,
    { apiId: string }
  > = (props) => {
    const { apiId } = props ?? {};

    return deleteAPI(apiId);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteAPIMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useDeleteAPIHook>>>
>;

export type DeleteAPIMutationError =
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse;

/**
 * @summary Delete API
 */
export const useDeleteAPI = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useDeleteAPIHook>>>,
      TError,
      { apiId: string },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useDeleteAPIHook>>>,
  TError,
  { apiId: string },
  TContext
> => {
  const mutationOptions = useDeleteAPIMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Replace the existing permissions for an API.
The provided permissions array represents the complete set
of permissions that will be applied to the API after this operation.

 * @summary Update API permissions
 */
export const useUpdateAPIPermissionsHook = () => {
  const updateAPIPermissions = useAxiosAuth<APIPermissionDTO[]>();

  return useCallback(
    (
      apiId: string,
      updateAPIPermissionsRequestDTO: UpdateAPIPermissionsRequestDTO,
    ) => {
      return updateAPIPermissions({
        url: `/admin/apis/${apiId}/permissions`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        data: updateAPIPermissionsRequestDTO,
      });
    },
    [updateAPIPermissions],
  );
};

export const useUpdateAPIPermissionsMutationOptions = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdateAPIPermissionsHook>>>,
    TError,
    { apiId: string; data: UpdateAPIPermissionsRequestDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useUpdateAPIPermissionsHook>>>,
  TError,
  { apiId: string; data: UpdateAPIPermissionsRequestDTO },
  TContext
> => {
  const mutationKey = ['updateAPIPermissions'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const updateAPIPermissions = useUpdateAPIPermissionsHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useUpdateAPIPermissionsHook>>>,
    { apiId: string; data: UpdateAPIPermissionsRequestDTO }
  > = (props) => {
    const { apiId, data } = props ?? {};

    return updateAPIPermissions(apiId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateAPIPermissionsMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useUpdateAPIPermissionsHook>>>
>;
export type UpdateAPIPermissionsMutationBody = UpdateAPIPermissionsRequestDTO;
export type UpdateAPIPermissionsMutationError =
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse;

/**
 * @summary Update API permissions
 */
export const useUpdateAPIPermissions = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useUpdateAPIPermissionsHook>>>,
      TError,
      { apiId: string; data: UpdateAPIPermissionsRequestDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useUpdateAPIPermissionsHook>>>,
  TError,
  { apiId: string; data: UpdateAPIPermissionsRequestDTO },
  TContext
> => {
  const mutationOptions = useUpdateAPIPermissionsMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Replace the existing audiences for an API.
The provided audience array represents the complete set of audiences
that will be associated with the API after this operation.

 * @summary Update API audiences
 */
export const useUpdateAPIAudiencesHook = () => {
  const updateAPIAudiences = useAxiosAuth<APIAudienceDTO[]>();

  return useCallback(
    (
      apiId: string,
      updateAPIAudiencesRequestDTO: UpdateAPIAudiencesRequestDTO,
    ) => {
      return updateAPIAudiences({
        url: `/admin/apis/${apiId}/audiences`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        data: updateAPIAudiencesRequestDTO,
      });
    },
    [updateAPIAudiences],
  );
};

export const useUpdateAPIAudiencesMutationOptions = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdateAPIAudiencesHook>>>,
    TError,
    { apiId: string; data: UpdateAPIAudiencesRequestDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useUpdateAPIAudiencesHook>>>,
  TError,
  { apiId: string; data: UpdateAPIAudiencesRequestDTO },
  TContext
> => {
  const mutationKey = ['updateAPIAudiences'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const updateAPIAudiences = useUpdateAPIAudiencesHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useUpdateAPIAudiencesHook>>>,
    { apiId: string; data: UpdateAPIAudiencesRequestDTO }
  > = (props) => {
    const { apiId, data } = props ?? {};

    return updateAPIAudiences(apiId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateAPIAudiencesMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useUpdateAPIAudiencesHook>>>
>;
export type UpdateAPIAudiencesMutationBody = UpdateAPIAudiencesRequestDTO;
export type UpdateAPIAudiencesMutationError =
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse;

/**
 * @summary Update API audiences
 */
export const useUpdateAPIAudiences = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useUpdateAPIAudiencesHook>>>,
      TError,
      { apiId: string; data: UpdateAPIAudiencesRequestDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useUpdateAPIAudiencesHook>>>,
  TError,
  { apiId: string; data: UpdateAPIAudiencesRequestDTO },
  TContext
> => {
  const mutationOptions = useUpdateAPIAudiencesMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * @summary Invite a new user
 */
export const useInviteUserHook = () => {
  const inviteUser = useAxiosAuth<InviteUserSuccessResponseDTO>();

  return useCallback(
    (inviteUserRequestDTO: InviteUserRequestDTO, signal?: AbortSignal) => {
      return inviteUser({
        url: `/admin/invite-user`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: inviteUserRequestDTO,
        signal,
      });
    },
    [inviteUser],
  );
};

export const useInviteUserMutationOptions = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | InviteUserConflictResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useInviteUserHook>>>,
    TError,
    { data: InviteUserRequestDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useInviteUserHook>>>,
  TError,
  { data: InviteUserRequestDTO },
  TContext
> => {
  const mutationKey = ['inviteUser'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const inviteUser = useInviteUserHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useInviteUserHook>>>,
    { data: InviteUserRequestDTO }
  > = (props) => {
    const { data } = props ?? {};

    return inviteUser(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type InviteUserMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useInviteUserHook>>>
>;
export type InviteUserMutationBody = InviteUserRequestDTO;
export type InviteUserMutationError =
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | InviteUserConflictResponse;

/**
 * @summary Invite a new user
 */
export const useInviteUser = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | InviteUserConflictResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useInviteUserHook>>>,
      TError,
      { data: InviteUserRequestDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useInviteUserHook>>>,
  TError,
  { data: InviteUserRequestDTO },
  TContext
> => {
  const mutationOptions = useInviteUserMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * @summary Invite a new user with a user profile
 */
export const useInviteUserWithUserProfileHook = () => {
  const inviteUserWithUserProfile =
    useAxiosAuth<InviteUserSuccessResponseDTO>();

  return useCallback(
    (
      tenantId: string,
      inviteUserWithUserProfileRequestDTO: InviteUserWithUserProfileRequestDTO,
      signal?: AbortSignal,
    ) => {
      return inviteUserWithUserProfile({
        url: `/admin/tenants/${tenantId}/invite-user-with-user-profile`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: inviteUserWithUserProfileRequestDTO,
        signal,
      });
    },
    [inviteUserWithUserProfile],
  );
};

export const useInviteUserWithUserProfileMutationOptions = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | InviteUserConflictResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useInviteUserWithUserProfileHook>>>,
    TError,
    { tenantId: string; data: InviteUserWithUserProfileRequestDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useInviteUserWithUserProfileHook>>>,
  TError,
  { tenantId: string; data: InviteUserWithUserProfileRequestDTO },
  TContext
> => {
  const mutationKey = ['inviteUserWithUserProfile'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const inviteUserWithUserProfile = useInviteUserWithUserProfileHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useInviteUserWithUserProfileHook>>>,
    { tenantId: string; data: InviteUserWithUserProfileRequestDTO }
  > = (props) => {
    const { tenantId, data } = props ?? {};

    return inviteUserWithUserProfile(tenantId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type InviteUserWithUserProfileMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useInviteUserWithUserProfileHook>>>
>;
export type InviteUserWithUserProfileMutationBody =
  InviteUserWithUserProfileRequestDTO;
export type InviteUserWithUserProfileMutationError =
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | InviteUserConflictResponse;

/**
 * @summary Invite a new user with a user profile
 */
export const useInviteUserWithUserProfile = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | InviteUserConflictResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useInviteUserWithUserProfileHook>>>,
      TError,
      { tenantId: string; data: InviteUserWithUserProfileRequestDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useInviteUserWithUserProfileHook>>>,
  TError,
  { tenantId: string; data: InviteUserWithUserProfileRequestDTO },
  TContext
> => {
  const mutationOptions = useInviteUserWithUserProfileMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Like `InviteUserWithUserProfile`, but creates the Kratos identity when none exists and
always returns `user_id`. An address on an IdP-only domain is rejected with 400, since that
user gets no Kratos identity until their first IdP sign-in; invite them with
`InviteUserWithUserProfile` instead.

 * @summary Create a user with a user profile
 */
export const useCreateUserWithUserProfileHook = () => {
  const createUserWithUserProfile =
    useAxiosAuth<CreateUserSuccessResponseDTO>();

  return useCallback(
    (
      tenantId: string,
      inviteUserWithUserProfileRequestDTO: InviteUserWithUserProfileRequestDTO,
      signal?: AbortSignal,
    ) => {
      return createUserWithUserProfile({
        url: `/admin/tenants/${tenantId}/create-user-with-user-profile`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: inviteUserWithUserProfileRequestDTO,
        signal,
      });
    },
    [createUserWithUserProfile],
  );
};

export const useCreateUserWithUserProfileMutationOptions = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | CreateUserConflictResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useCreateUserWithUserProfileHook>>>,
    TError,
    { tenantId: string; data: InviteUserWithUserProfileRequestDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useCreateUserWithUserProfileHook>>>,
  TError,
  { tenantId: string; data: InviteUserWithUserProfileRequestDTO },
  TContext
> => {
  const mutationKey = ['createUserWithUserProfile'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const createUserWithUserProfile = useCreateUserWithUserProfileHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useCreateUserWithUserProfileHook>>>,
    { tenantId: string; data: InviteUserWithUserProfileRequestDTO }
  > = (props) => {
    const { tenantId, data } = props ?? {};

    return createUserWithUserProfile(tenantId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateUserWithUserProfileMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useCreateUserWithUserProfileHook>>>
>;
export type CreateUserWithUserProfileMutationBody =
  InviteUserWithUserProfileRequestDTO;
export type CreateUserWithUserProfileMutationError =
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | CreateUserConflictResponse;

/**
 * @summary Create a user with a user profile
 */
export const useCreateUserWithUserProfile = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | CreateUserConflictResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useCreateUserWithUserProfileHook>>>,
      TError,
      { tenantId: string; data: InviteUserWithUserProfileRequestDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useCreateUserWithUserProfileHook>>>,
  TError,
  { tenantId: string; data: InviteUserWithUserProfileRequestDTO },
  TContext
> => {
  const mutationOptions = useCreateUserWithUserProfileMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Find users for tenant. Optional parameters
can be used to filter the users
by user_ids or emails and organization, role_ids and application_id.
If no user_ids or emails are provided, all users are returned.
If full is set to true, each users matching profiles
are returned as an array on the user.

 * @summary Find users with profiles
 */
export const useFindUsersHook = () => {
  const findUsers = useAxiosAuth<UserDTO[]>();

  return useCallback(
    (tenantId: string, params?: FindUsersParams, signal?: AbortSignal) => {
      return findUsers({
        url: `/admin/tenants/${tenantId}/users`,
        method: 'GET',
        params,
        signal,
      });
    },
    [findUsers],
  );
};

export const getFindUsersQueryKey = (
  tenantId?: string,
  params?: FindUsersParams,
) => {
  return [
    `/admin/tenants/${tenantId}/users`,
    ...(params ? [params] : []),
  ] as const;
};

export const useFindUsersQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useFindUsersHook>>>,
  TError = BadRequestResponse | UnauthorizedResponse | ForbiddenResponse,
>(
  tenantId: string,
  params?: FindUsersParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useFindUsersHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getFindUsersQueryKey(tenantId, params);

  const findUsers = useFindUsersHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useFindUsersHook>>>
  > = ({ signal }) => findUsers(tenantId, params, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!tenantId,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useFindUsersHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type FindUsersQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useFindUsersHook>>>
>;
export type FindUsersQueryError =
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse;

export function useFindUsers<
  TData = Awaited<ReturnType<ReturnType<typeof useFindUsersHook>>>,
  TError = BadRequestResponse | UnauthorizedResponse | ForbiddenResponse,
>(
  tenantId: string,
  params: undefined | FindUsersParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useFindUsersHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useFindUsersHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useFindUsersHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useFindUsers<
  TData = Awaited<ReturnType<ReturnType<typeof useFindUsersHook>>>,
  TError = BadRequestResponse | UnauthorizedResponse | ForbiddenResponse,
>(
  tenantId: string,
  params?: FindUsersParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useFindUsersHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useFindUsersHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useFindUsersHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useFindUsers<
  TData = Awaited<ReturnType<ReturnType<typeof useFindUsersHook>>>,
  TError = BadRequestResponse | UnauthorizedResponse | ForbiddenResponse,
>(
  tenantId: string,
  params?: FindUsersParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useFindUsersHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Find users with profiles
 */

export function useFindUsers<
  TData = Awaited<ReturnType<ReturnType<typeof useFindUsersHook>>>,
  TError = BadRequestResponse | UnauthorizedResponse | ForbiddenResponse,
>(
  tenantId: string,
  params?: FindUsersParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useFindUsersHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useFindUsersQueryOptions(tenantId, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Lists all user profiles for a specific email,
all user profiles belonging to the user are listed regardless of tenant and organization.

 * @summary List all user profiles for a users email
 */
export const useListAllUserProfilesForEmailHook = () => {
  const listAllUserProfilesForEmail = useAxiosAuth<UserProfileDTO[]>();

  return useCallback(
    (email: string, signal?: AbortSignal) => {
      return listAllUserProfilesForEmail({
        url: `/admin/userprofiles/email/${email}`,
        method: 'GET',
        signal,
      });
    },
    [listAllUserProfilesForEmail],
  );
};

export const getListAllUserProfilesForEmailQueryKey = (email?: string) => {
  return [`/admin/userprofiles/email/${email}`] as const;
};

export const useListAllUserProfilesForEmailQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useListAllUserProfilesForEmailHook>>
  >,
  TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse,
>(
  email: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useListAllUserProfilesForEmailHook>>
        >,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListAllUserProfilesForEmailQueryKey(email);

  const listAllUserProfilesForEmail = useListAllUserProfilesForEmailHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useListAllUserProfilesForEmailHook>>>
  > = ({ signal }) => listAllUserProfilesForEmail(email, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!email,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useListAllUserProfilesForEmailHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type ListAllUserProfilesForEmailQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useListAllUserProfilesForEmailHook>>>
>;
export type ListAllUserProfilesForEmailQueryError =
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse;

export function useListAllUserProfilesForEmail<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListAllUserProfilesForEmailHook>>
  >,
  TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse,
>(
  email: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useListAllUserProfilesForEmailHook>>
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useListAllUserProfilesForEmailHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useListAllUserProfilesForEmailHook>>
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useListAllUserProfilesForEmail<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListAllUserProfilesForEmailHook>>
  >,
  TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse,
>(
  email: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useListAllUserProfilesForEmailHook>>
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useListAllUserProfilesForEmailHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useListAllUserProfilesForEmailHook>>
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useListAllUserProfilesForEmail<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListAllUserProfilesForEmailHook>>
  >,
  TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse,
>(
  email: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useListAllUserProfilesForEmailHook>>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary List all user profiles for a users email
 */

export function useListAllUserProfilesForEmail<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListAllUserProfilesForEmailHook>>
  >,
  TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse,
>(
  email: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useListAllUserProfilesForEmailHook>>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useListAllUserProfilesForEmailQueryOptions(
    email,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Create a new organization for the tenant the user is
authenticated as.
Only users that do not belong to an organization
can create organizations.
The organization is linked to the given application, which must be
enabled and active for the tenant and have organization_management
`internal`; `external` and `disabled` applications return 403.

 * @summary Create a new organization
 */
export const useCreateOrganizationHook = () => {
  const createOrganization = useAxiosAuth<OrganizationDTO>();

  return useCallback(
    (
      createOrganizationRequestDTO: CreateOrganizationRequestDTO,
      signal?: AbortSignal,
    ) => {
      return createOrganization({
        url: `/manage/organizations`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: createOrganizationRequestDTO,
        signal,
      });
    },
    [createOrganization],
  );
};

export const useCreateOrganizationMutationOptions = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useCreateOrganizationHook>>>,
    TError,
    { data: CreateOrganizationRequestDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useCreateOrganizationHook>>>,
  TError,
  { data: CreateOrganizationRequestDTO },
  TContext
> => {
  const mutationKey = ['createOrganization'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const createOrganization = useCreateOrganizationHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useCreateOrganizationHook>>>,
    { data: CreateOrganizationRequestDTO }
  > = (props) => {
    const { data } = props ?? {};

    return createOrganization(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateOrganizationMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useCreateOrganizationHook>>>
>;
export type CreateOrganizationMutationBody = CreateOrganizationRequestDTO;
export type CreateOrganizationMutationError =
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse;

/**
 * @summary Create a new organization
 */
export const useCreateOrganization = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useCreateOrganizationHook>>>,
      TError,
      { data: CreateOrganizationRequestDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useCreateOrganizationHook>>>,
  TError,
  { data: CreateOrganizationRequestDTO },
  TContext
> => {
  const mutationOptions = useCreateOrganizationMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Lists all organizations for the tenant the user is
authenticated as.
Only accessible to users that do not belong to an organization.

 * @summary List organizations for a users tenant
 */
export const useListOrganizationsHook = () => {
  const listOrganizations = useAxiosAuth<OrganizationDTO[]>();

  return useCallback(
    (signal?: AbortSignal) => {
      return listOrganizations({
        url: `/manage/organizations`,
        method: 'GET',
        signal,
      });
    },
    [listOrganizations],
  );
};

export const getListOrganizationsQueryKey = () => {
  return [`/manage/organizations`] as const;
};

export const useListOrganizationsQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useListOrganizationsHook>>>,
  TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<ReturnType<typeof useListOrganizationsHook>>>,
      TError,
      TData
    >
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListOrganizationsQueryKey();

  const listOrganizations = useListOrganizationsHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useListOrganizationsHook>>>
  > = ({ signal }) => listOrganizations(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useListOrganizationsHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type ListOrganizationsQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useListOrganizationsHook>>>
>;
export type ListOrganizationsQueryError =
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse;

export function useListOrganizations<
  TData = Awaited<ReturnType<ReturnType<typeof useListOrganizationsHook>>>,
  TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListOrganizationsHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useListOrganizationsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useListOrganizationsHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useListOrganizations<
  TData = Awaited<ReturnType<ReturnType<typeof useListOrganizationsHook>>>,
  TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListOrganizationsHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useListOrganizationsHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useListOrganizationsHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useListOrganizations<
  TData = Awaited<ReturnType<ReturnType<typeof useListOrganizationsHook>>>,
  TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListOrganizationsHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary List organizations for a users tenant
 */

export function useListOrganizations<
  TData = Awaited<ReturnType<ReturnType<typeof useListOrganizationsHook>>>,
  TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListOrganizationsHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useListOrganizationsQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Get a specific organization for the tenant the user is
authenticated as.
Only accessible to users that do not belong to an organization.

 * @summary Get a specific organization
 */
export const useGetOrganizationHook = () => {
  const getOrganization = useAxiosAuth<OrganizationDTO>();

  return useCallback(
    (organizationId: string, signal?: AbortSignal) => {
      return getOrganization({
        url: `/manage/organizations/${organizationId}`,
        method: 'GET',
        signal,
      });
    },
    [getOrganization],
  );
};

export const getGetOrganizationQueryKey = (organizationId?: string) => {
  return [`/manage/organizations/${organizationId}`] as const;
};

export const useGetOrganizationQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetOrganizationHook>>>,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(
  organizationId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetOrganizationHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetOrganizationQueryKey(organizationId);

  const getOrganization = useGetOrganizationHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGetOrganizationHook>>>
  > = ({ signal }) => getOrganization(organizationId, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!organizationId,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetOrganizationHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetOrganizationQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetOrganizationHook>>>
>;
export type GetOrganizationQueryError =
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse;

export function useGetOrganization<
  TData = Awaited<ReturnType<ReturnType<typeof useGetOrganizationHook>>>,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(
  organizationId: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetOrganizationHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetOrganizationHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetOrganizationHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetOrganization<
  TData = Awaited<ReturnType<ReturnType<typeof useGetOrganizationHook>>>,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(
  organizationId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetOrganizationHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetOrganizationHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetOrganizationHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetOrganization<
  TData = Awaited<ReturnType<ReturnType<typeof useGetOrganizationHook>>>,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(
  organizationId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetOrganizationHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get a specific organization
 */

export function useGetOrganization<
  TData = Awaited<ReturnType<ReturnType<typeof useGetOrganizationHook>>>,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(
  organizationId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetOrganizationHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useGetOrganizationQueryOptions(organizationId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Update an organization for the tenant the user is authenticated as.
Only accessible to users that do not belong to an organization.
Returns 403 unless the organization is linked to an application whose
organization_management is `internal`.

 * @summary Update tenant organization
 */
export const useUpdateOrganizationHook = () => {
  const updateOrganization = useAxiosAuth<OrganizationDTO>();

  return useCallback(
    (
      organizationId: string,
      updateOrganizationRequestDTO: UpdateOrganizationRequestDTO,
    ) => {
      return updateOrganization({
        url: `/manage/organizations/${organizationId}`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        data: updateOrganizationRequestDTO,
      });
    },
    [updateOrganization],
  );
};

export const useUpdateOrganizationMutationOptions = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdateOrganizationHook>>>,
    TError,
    { organizationId: string; data: UpdateOrganizationRequestDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useUpdateOrganizationHook>>>,
  TError,
  { organizationId: string; data: UpdateOrganizationRequestDTO },
  TContext
> => {
  const mutationKey = ['updateOrganization'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const updateOrganization = useUpdateOrganizationHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useUpdateOrganizationHook>>>,
    { organizationId: string; data: UpdateOrganizationRequestDTO }
  > = (props) => {
    const { organizationId, data } = props ?? {};

    return updateOrganization(organizationId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateOrganizationMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useUpdateOrganizationHook>>>
>;
export type UpdateOrganizationMutationBody = UpdateOrganizationRequestDTO;
export type UpdateOrganizationMutationError =
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse;

/**
 * @summary Update tenant organization
 */
export const useUpdateOrganization = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useUpdateOrganizationHook>>>,
      TError,
      { organizationId: string; data: UpdateOrganizationRequestDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useUpdateOrganizationHook>>>,
  TError,
  { organizationId: string; data: UpdateOrganizationRequestDTO },
  TContext
> => {
  const mutationOptions = useUpdateOrganizationMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Delete a organization for the tenant the user is
authenticated as.
Only accessible to users that do not belong to an organization.
Returns 403 unless the organization is linked to an application whose
organization_management is `internal`.

 * @summary Delete organization
 */
export const useDeleteOrganizationHook = () => {
  const deleteOrganization = useAxiosAuth<OrganizationDTO>();

  return useCallback(
    (organizationId: string) => {
      return deleteOrganization({
        url: `/manage/organizations/${organizationId}`,
        method: 'DELETE',
      });
    },
    [deleteOrganization],
  );
};

export const useDeleteOrganizationMutationOptions = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteOrganizationHook>>>,
    TError,
    { organizationId: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useDeleteOrganizationHook>>>,
  TError,
  { organizationId: string },
  TContext
> => {
  const mutationKey = ['deleteOrganization'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const deleteOrganization = useDeleteOrganizationHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useDeleteOrganizationHook>>>,
    { organizationId: string }
  > = (props) => {
    const { organizationId } = props ?? {};

    return deleteOrganization(organizationId);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteOrganizationMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useDeleteOrganizationHook>>>
>;

export type DeleteOrganizationMutationError =
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse;

/**
 * @summary Delete organization
 */
export const useDeleteOrganization = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useDeleteOrganizationHook>>>,
      TError,
      { organizationId: string },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useDeleteOrganizationHook>>>,
  TError,
  { organizationId: string },
  TContext
> => {
  const mutationOptions = useDeleteOrganizationMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * @summary Create a new user profile
 */
export const useCreateUserProfileHook = () => {
  const createUserProfile = useAxiosAuth<UserProfileDTO>();

  return useCallback(
    (
      createUserProfileRequestDTO: CreateUserProfileRequestDTO,
      signal?: AbortSignal,
    ) => {
      return createUserProfile({
        url: `/manage/userprofiles`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: createUserProfileRequestDTO,
        signal,
      });
    },
    [createUserProfile],
  );
};

export const useCreateUserProfileMutationOptions = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UserProfileConflictResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useCreateUserProfileHook>>>,
    TError,
    { data: CreateUserProfileRequestDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useCreateUserProfileHook>>>,
  TError,
  { data: CreateUserProfileRequestDTO },
  TContext
> => {
  const mutationKey = ['createUserProfile'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const createUserProfile = useCreateUserProfileHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useCreateUserProfileHook>>>,
    { data: CreateUserProfileRequestDTO }
  > = (props) => {
    const { data } = props ?? {};

    return createUserProfile(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateUserProfileMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useCreateUserProfileHook>>>
>;
export type CreateUserProfileMutationBody = CreateUserProfileRequestDTO;
export type CreateUserProfileMutationError =
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
  | UserProfileConflictResponse;

/**
 * @summary Create a new user profile
 */
export const useCreateUserProfile = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UserProfileConflictResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useCreateUserProfileHook>>>,
      TError,
      { data: CreateUserProfileRequestDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useCreateUserProfileHook>>>,
  TError,
  { data: CreateUserProfileRequestDTO },
  TContext
> => {
  const mutationOptions = useCreateUserProfileMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Lists all user profiles for the tenant and organization
the user is authenticated as. If the user has no organization,
user profiles for all organizations in that tenant are retrieved.

 * @summary List user profiles for a users tenant
 */
export const useListUserProfilesForTenantHook = () => {
  const listUserProfilesForTenant = useAxiosAuth<UserProfileDTO[]>();

  return useCallback(
    (signal?: AbortSignal) => {
      return listUserProfilesForTenant({
        url: `/manage/userprofiles`,
        method: 'GET',
        signal,
      });
    },
    [listUserProfilesForTenant],
  );
};

export const getListUserProfilesForTenantQueryKey = () => {
  return [`/manage/userprofiles`] as const;
};

export const useListUserProfilesForTenantQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useListUserProfilesForTenantHook>>
  >,
  TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<ReturnType<typeof useListUserProfilesForTenantHook>>>,
      TError,
      TData
    >
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListUserProfilesForTenantQueryKey();

  const listUserProfilesForTenant = useListUserProfilesForTenantHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useListUserProfilesForTenantHook>>>
  > = ({ signal }) => listUserProfilesForTenant(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useListUserProfilesForTenantHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type ListUserProfilesForTenantQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useListUserProfilesForTenantHook>>>
>;
export type ListUserProfilesForTenantQueryError =
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse;

export function useListUserProfilesForTenant<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListUserProfilesForTenantHook>>
  >,
  TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useListUserProfilesForTenantHook>>
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useListUserProfilesForTenantHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useListUserProfilesForTenantHook>>
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useListUserProfilesForTenant<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListUserProfilesForTenantHook>>
  >,
  TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useListUserProfilesForTenantHook>>
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useListUserProfilesForTenantHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useListUserProfilesForTenantHook>>
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useListUserProfilesForTenant<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListUserProfilesForTenantHook>>
  >,
  TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useListUserProfilesForTenantHook>>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary List user profiles for a users tenant
 */

export function useListUserProfilesForTenant<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListUserProfilesForTenantHook>>
  >,
  TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useListUserProfilesForTenantHook>>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useListUserProfilesForTenantQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Get a specific user profile
 */
export const useGetUserProfileHook = () => {
  const getUserProfile = useAxiosAuth<UserProfileDTO>();

  return useCallback(
    (userProfileId: string, signal?: AbortSignal) => {
      return getUserProfile({
        url: `/manage/userprofiles/${userProfileId}`,
        method: 'GET',
        signal,
      });
    },
    [getUserProfile],
  );
};

export const getGetUserProfileQueryKey = (userProfileId?: string) => {
  return [`/manage/userprofiles/${userProfileId}`] as const;
};

export const useGetUserProfileQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useGetUserProfileHook>>>,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(
  userProfileId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetUserProfileHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetUserProfileQueryKey(userProfileId);

  const getUserProfile = useGetUserProfileHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useGetUserProfileHook>>>
  > = ({ signal }) => getUserProfile(userProfileId, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!userProfileId,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useGetUserProfileHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetUserProfileQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useGetUserProfileHook>>>
>;
export type GetUserProfileQueryError =
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse;

export function useGetUserProfile<
  TData = Awaited<ReturnType<ReturnType<typeof useGetUserProfileHook>>>,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(
  userProfileId: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetUserProfileHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetUserProfileHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetUserProfileHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useGetUserProfile<
  TData = Awaited<ReturnType<ReturnType<typeof useGetUserProfileHook>>>,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(
  userProfileId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetUserProfileHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetUserProfileHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetUserProfileHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetUserProfile<
  TData = Awaited<ReturnType<ReturnType<typeof useGetUserProfileHook>>>,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(
  userProfileId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetUserProfileHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get a specific user profile
 */

export function useGetUserProfile<
  TData = Awaited<ReturnType<ReturnType<typeof useGetUserProfileHook>>>,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(
  userProfileId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetUserProfileHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useGetUserProfileQueryOptions(userProfileId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Update a user profile
 */
export const useUpdateUserProfileHook = () => {
  const updateUserProfile = useAxiosAuth<UserProfileDTO>();

  return useCallback(
    (
      userProfileId: string,
      updateUserProfileRequestDTO: UpdateUserProfileRequestDTO,
    ) => {
      return updateUserProfile({
        url: `/manage/userprofiles/${userProfileId}`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        data: updateUserProfileRequestDTO,
      });
    },
    [updateUserProfile],
  );
};

export const useUpdateUserProfileMutationOptions = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdateUserProfileHook>>>,
    TError,
    { userProfileId: string; data: UpdateUserProfileRequestDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useUpdateUserProfileHook>>>,
  TError,
  { userProfileId: string; data: UpdateUserProfileRequestDTO },
  TContext
> => {
  const mutationKey = ['updateUserProfile'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const updateUserProfile = useUpdateUserProfileHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useUpdateUserProfileHook>>>,
    { userProfileId: string; data: UpdateUserProfileRequestDTO }
  > = (props) => {
    const { userProfileId, data } = props ?? {};

    return updateUserProfile(userProfileId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateUserProfileMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useUpdateUserProfileHook>>>
>;
export type UpdateUserProfileMutationBody = UpdateUserProfileRequestDTO;
export type UpdateUserProfileMutationError =
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse;

/**
 * @summary Update a user profile
 */
export const useUpdateUserProfile = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useUpdateUserProfileHook>>>,
      TError,
      { userProfileId: string; data: UpdateUserProfileRequestDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useUpdateUserProfileHook>>>,
  TError,
  { userProfileId: string; data: UpdateUserProfileRequestDTO },
  TContext
> => {
  const mutationOptions = useUpdateUserProfileMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * @summary Delete a user profile
 */
export const useDeleteUserProfileHook = () => {
  const deleteUserProfile = useAxiosAuth<UserProfileDTO>();

  return useCallback(
    (userProfileId: string) => {
      return deleteUserProfile({
        url: `/manage/userprofiles/${userProfileId}`,
        method: 'DELETE',
      });
    },
    [deleteUserProfile],
  );
};

export const useDeleteUserProfileMutationOptions = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteUserProfileHook>>>,
    TError,
    { userProfileId: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<ReturnType<typeof useDeleteUserProfileHook>>>,
  TError,
  { userProfileId: string },
  TContext
> => {
  const mutationKey = ['deleteUserProfile'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const deleteUserProfile = useDeleteUserProfileHook();

  const mutationFn: MutationFunction<
    Awaited<ReturnType<ReturnType<typeof useDeleteUserProfileHook>>>,
    { userProfileId: string }
  > = (props) => {
    const { userProfileId } = props ?? {};

    return deleteUserProfile(userProfileId);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteUserProfileMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useDeleteUserProfileHook>>>
>;

export type DeleteUserProfileMutationError =
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse;

/**
 * @summary Delete a user profile
 */
export const useDeleteUserProfile = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<ReturnType<typeof useDeleteUserProfileHook>>>,
      TError,
      { userProfileId: string },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<ReturnType<typeof useDeleteUserProfileHook>>>,
  TError,
  { userProfileId: string },
  TContext
> => {
  const mutationOptions = useDeleteUserProfileMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Lists user profiles for a specific email,
only user profiles in the requesting users tenant and organization
are retrieved. If the user has no organization,
user profiles for all organizations in that tenant are retrieved.

 * @summary List user profiles for a users email
 */
export const useListUserProfilesForEmailHook = () => {
  const listUserProfilesForEmail = useAxiosAuth<UserProfileDTO[]>();

  return useCallback(
    (email: string, signal?: AbortSignal) => {
      return listUserProfilesForEmail({
        url: `/manage/userprofiles/email/${email}`,
        method: 'GET',
        signal,
      });
    },
    [listUserProfilesForEmail],
  );
};

export const getListUserProfilesForEmailQueryKey = (email?: string) => {
  return [`/manage/userprofiles/email/${email}`] as const;
};

export const useListUserProfilesForEmailQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useListUserProfilesForEmailHook>>
  >,
  TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse,
>(
  email: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListUserProfilesForEmailHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListUserProfilesForEmailQueryKey(email);

  const listUserProfilesForEmail = useListUserProfilesForEmailHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useListUserProfilesForEmailHook>>>
  > = ({ signal }) => listUserProfilesForEmail(email, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!email,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useListUserProfilesForEmailHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type ListUserProfilesForEmailQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useListUserProfilesForEmailHook>>>
>;
export type ListUserProfilesForEmailQueryError =
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse;

export function useListUserProfilesForEmail<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListUserProfilesForEmailHook>>
  >,
  TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse,
>(
  email: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListUserProfilesForEmailHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useListUserProfilesForEmailHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useListUserProfilesForEmailHook>>
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useListUserProfilesForEmail<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListUserProfilesForEmailHook>>
  >,
  TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse,
>(
  email: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListUserProfilesForEmailHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useListUserProfilesForEmailHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useListUserProfilesForEmailHook>>
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useListUserProfilesForEmail<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListUserProfilesForEmailHook>>
  >,
  TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse,
>(
  email: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListUserProfilesForEmailHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary List user profiles for a users email
 */

export function useListUserProfilesForEmail<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListUserProfilesForEmailHook>>
  >,
  TError = UnauthorizedResponse | ForbiddenResponse | NotFoundResponse,
>(
  email: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useListUserProfilesForEmailHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useListUserProfilesForEmailQueryOptions(email, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary List applications for users tenant
 */
export const useListApplicationsForTenantHook = () => {
  const listApplicationsForTenant = useAxiosAuth<ApplicationDTO[]>();

  return useCallback(
    (signal?: AbortSignal) => {
      return listApplicationsForTenant({
        url: `/manage/applications`,
        method: 'GET',
        signal,
      });
    },
    [listApplicationsForTenant],
  );
};

export const getListApplicationsForTenantQueryKey = () => {
  return [`/manage/applications`] as const;
};

export const useListApplicationsForTenantQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useListApplicationsForTenantHook>>
  >,
  TError = UnauthorizedResponse | ForbiddenResponse,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<ReturnType<typeof useListApplicationsForTenantHook>>>,
      TError,
      TData
    >
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListApplicationsForTenantQueryKey();

  const listApplicationsForTenant = useListApplicationsForTenantHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useListApplicationsForTenantHook>>>
  > = ({ signal }) => listApplicationsForTenant(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useListApplicationsForTenantHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type ListApplicationsForTenantQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useListApplicationsForTenantHook>>>
>;
export type ListApplicationsForTenantQueryError =
  | UnauthorizedResponse
  | ForbiddenResponse;

export function useListApplicationsForTenant<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListApplicationsForTenantHook>>
  >,
  TError = UnauthorizedResponse | ForbiddenResponse,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useListApplicationsForTenantHook>>
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useListApplicationsForTenantHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useListApplicationsForTenantHook>>
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useListApplicationsForTenant<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListApplicationsForTenantHook>>
  >,
  TError = UnauthorizedResponse | ForbiddenResponse,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useListApplicationsForTenantHook>>
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<ReturnType<typeof useListApplicationsForTenantHook>>
          >,
          TError,
          Awaited<
            ReturnType<ReturnType<typeof useListApplicationsForTenantHook>>
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useListApplicationsForTenant<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListApplicationsForTenantHook>>
  >,
  TError = UnauthorizedResponse | ForbiddenResponse,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useListApplicationsForTenantHook>>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary List applications for users tenant
 */

export function useListApplicationsForTenant<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListApplicationsForTenantHook>>
  >,
  TError = UnauthorizedResponse | ForbiddenResponse,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<ReturnType<typeof useListApplicationsForTenantHook>>
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useListApplicationsForTenantQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Lists roles that the user has permission to assign for the application.

 * @summary List assignable roles for a specific application
 */
export const useListAssignableRolesForApplicationHook = () => {
  const listAssignableRolesForApplication = useAxiosAuth<RoleDTO[]>();

  return useCallback(
    (applicationId: string, signal?: AbortSignal) => {
      return listAssignableRolesForApplication({
        url: `/manage/applications/${applicationId}/roles`,
        method: 'GET',
        signal,
      });
    },
    [listAssignableRolesForApplication],
  );
};

export const getListAssignableRolesForApplicationQueryKey = (
  applicationId?: string,
) => {
  return [`/manage/applications/${applicationId}/roles`] as const;
};

export const useListAssignableRolesForApplicationQueryOptions = <
  TData = Awaited<
    ReturnType<ReturnType<typeof useListAssignableRolesForApplicationHook>>
  >,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(
  applicationId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<typeof useListAssignableRolesForApplicationHook>
          >
        >,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getListAssignableRolesForApplicationQueryKey(applicationId);

  const listAssignableRolesForApplication =
    useListAssignableRolesForApplicationHook();

  const queryFn: QueryFunction<
    Awaited<
      ReturnType<ReturnType<typeof useListAssignableRolesForApplicationHook>>
    >
  > = ({ signal }) => listAssignableRolesForApplication(applicationId, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!applicationId,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<
      ReturnType<ReturnType<typeof useListAssignableRolesForApplicationHook>>
    >,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type ListAssignableRolesForApplicationQueryResult = NonNullable<
  Awaited<
    ReturnType<ReturnType<typeof useListAssignableRolesForApplicationHook>>
  >
>;
export type ListAssignableRolesForApplicationQueryError =
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse;

export function useListAssignableRolesForApplication<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListAssignableRolesForApplicationHook>>
  >,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(
  applicationId: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<typeof useListAssignableRolesForApplicationHook>
          >
        >,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<
            ReturnType<
              ReturnType<typeof useListAssignableRolesForApplicationHook>
            >
          >,
          TError,
          Awaited<
            ReturnType<
              ReturnType<typeof useListAssignableRolesForApplicationHook>
            >
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useListAssignableRolesForApplication<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListAssignableRolesForApplicationHook>>
  >,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(
  applicationId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<typeof useListAssignableRolesForApplicationHook>
          >
        >,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<
            ReturnType<
              ReturnType<typeof useListAssignableRolesForApplicationHook>
            >
          >,
          TError,
          Awaited<
            ReturnType<
              ReturnType<typeof useListAssignableRolesForApplicationHook>
            >
          >
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useListAssignableRolesForApplication<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListAssignableRolesForApplicationHook>>
  >,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(
  applicationId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<typeof useListAssignableRolesForApplicationHook>
          >
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary List assignable roles for a specific application
 */

export function useListAssignableRolesForApplication<
  TData = Awaited<
    ReturnType<ReturnType<typeof useListAssignableRolesForApplicationHook>>
  >,
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse,
>(
  applicationId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<typeof useListAssignableRolesForApplicationHook>
          >
        >,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useListAssignableRolesForApplicationQueryOptions(
    applicationId,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Invites a new user and creates a user profile for them in the authenticated user's tenant.
The application name in the invitation email will be derived from the application_id.
Both tenant-level and organization-level users can use this endpoint.
Organization users cannot specify organization_id in the request body - their organization is used automatically.

 * @summary Invite a new user with a user profile
 */
export const useManageInviteUserWithUserProfileHook = () => {
  const manageInviteUserWithUserProfile =
    useAxiosAuth<InviteUserSuccessResponseDTO>();

  return useCallback(
    (
      manageInviteUserWithUserProfileRequestDTO: ManageInviteUserWithUserProfileRequestDTO,
      signal?: AbortSignal,
    ) => {
      return manageInviteUserWithUserProfile({
        url: `/manage/invite-user-with-user-profile`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: manageInviteUserWithUserProfileRequestDTO,
        signal,
      });
    },
    [manageInviteUserWithUserProfile],
  );
};

export const useManageInviteUserWithUserProfileMutationOptions = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | InviteUserConflictResponse,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<
      ReturnType<ReturnType<typeof useManageInviteUserWithUserProfileHook>>
    >,
    TError,
    { data: ManageInviteUserWithUserProfileRequestDTO },
    TContext
  >;
}): UseMutationOptions<
  Awaited<
    ReturnType<ReturnType<typeof useManageInviteUserWithUserProfileHook>>
  >,
  TError,
  { data: ManageInviteUserWithUserProfileRequestDTO },
  TContext
> => {
  const mutationKey = ['manageInviteUserWithUserProfile'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const manageInviteUserWithUserProfile =
    useManageInviteUserWithUserProfileHook();

  const mutationFn: MutationFunction<
    Awaited<
      ReturnType<ReturnType<typeof useManageInviteUserWithUserProfileHook>>
    >,
    { data: ManageInviteUserWithUserProfileRequestDTO }
  > = (props) => {
    const { data } = props ?? {};

    return manageInviteUserWithUserProfile(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type ManageInviteUserWithUserProfileMutationResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useManageInviteUserWithUserProfileHook>>>
>;
export type ManageInviteUserWithUserProfileMutationBody =
  ManageInviteUserWithUserProfileRequestDTO;
export type ManageInviteUserWithUserProfileMutationError =
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | InviteUserConflictResponse;

/**
 * @summary Invite a new user with a user profile
 */
export const useManageInviteUserWithUserProfile = <
  TError =
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | InviteUserConflictResponse,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<
        ReturnType<ReturnType<typeof useManageInviteUserWithUserProfileHook>>
      >,
      TError,
      { data: ManageInviteUserWithUserProfileRequestDTO },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<
    ReturnType<ReturnType<typeof useManageInviteUserWithUserProfileHook>>
  >,
  TError,
  { data: ManageInviteUserWithUserProfileRequestDTO },
  TContext
> => {
  const mutationOptions =
    useManageInviteUserWithUserProfileMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * Find users for the authenticated user's tenant. Optional parameters
can be used to filter the users by user_ids or emails and organization, role_ids and application_id.
If no user_ids or emails are provided, all users are returned.
If full is set to true, each user's matching profiles are returned as an array on the user.
Organization users cannot specify organization_ids - their organization is used automatically.

 * @summary Find users with profiles
 */
export const useManageFindUsersHook = () => {
  const manageFindUsers = useAxiosAuth<UserDTO[]>();

  return useCallback(
    (params?: ManageFindUsersParams, signal?: AbortSignal) => {
      return manageFindUsers({
        url: `/manage/users`,
        method: 'GET',
        params,
        signal,
      });
    },
    [manageFindUsers],
  );
};

export const getManageFindUsersQueryKey = (params?: ManageFindUsersParams) => {
  return [`/manage/users`, ...(params ? [params] : [])] as const;
};

export const useManageFindUsersQueryOptions = <
  TData = Awaited<ReturnType<ReturnType<typeof useManageFindUsersHook>>>,
  TError = BadRequestResponse | UnauthorizedResponse | ForbiddenResponse,
>(
  params?: ManageFindUsersParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useManageFindUsersHook>>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getManageFindUsersQueryKey(params);

  const manageFindUsers = useManageFindUsersHook();

  const queryFn: QueryFunction<
    Awaited<ReturnType<ReturnType<typeof useManageFindUsersHook>>>
  > = ({ signal }) => manageFindUsers(params, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<ReturnType<typeof useManageFindUsersHook>>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type ManageFindUsersQueryResult = NonNullable<
  Awaited<ReturnType<ReturnType<typeof useManageFindUsersHook>>>
>;
export type ManageFindUsersQueryError =
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse;

export function useManageFindUsers<
  TData = Awaited<ReturnType<ReturnType<typeof useManageFindUsersHook>>>,
  TError = BadRequestResponse | UnauthorizedResponse | ForbiddenResponse,
>(
  params: undefined | ManageFindUsersParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useManageFindUsersHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useManageFindUsersHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useManageFindUsersHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData>;
};
export function useManageFindUsers<
  TData = Awaited<ReturnType<ReturnType<typeof useManageFindUsersHook>>>,
  TError = BadRequestResponse | UnauthorizedResponse | ForbiddenResponse,
>(
  params?: ManageFindUsersParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useManageFindUsersHook>>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useManageFindUsersHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useManageFindUsersHook>>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useManageFindUsers<
  TData = Awaited<ReturnType<ReturnType<typeof useManageFindUsersHook>>>,
  TError = BadRequestResponse | UnauthorizedResponse | ForbiddenResponse,
>(
  params?: ManageFindUsersParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useManageFindUsersHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Find users with profiles
 */

export function useManageFindUsers<
  TData = Awaited<ReturnType<ReturnType<typeof useManageFindUsersHook>>>,
  TError = BadRequestResponse | UnauthorizedResponse | ForbiddenResponse,
>(
  params?: ManageFindUsersParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useManageFindUsersHook>>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = useManageFindUsersQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData> };

  query.queryKey = queryOptions.queryKey;

  return query;
}
